import {
  Bell,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Clipboard,
  Clock3,
  Download,
  Home,
  ImagePlus,
  KeyRound,
  Languages,
  LogOut,
  Megaphone,
  MessageSquare,
  Palmtree,
  Pencil,
  Plus,
  Save,
  Settings,
  Trash2,
  Upload,
  UserRound,
  Users,
  Utensils,
  WalletCards,
  X,
} from "lucide-react";
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import logo from "../../assets/brand/doctorsmeal-logo.png";
import {
  addDays,
  buildMonthGrid,
  datesBetween,
  filterBulkDates,
  isSunday,
  isWeekend,
  startOfWeekMonday,
} from "../shared/calendar";
import { canEditMealDate } from "../shared/cutoff";
import {
  canReportMealConsumption,
  isActualMealOrder,
  type MealConsumptionStatus,
} from "../shared/meal-consumption";
import { parseEmployeeImportFile } from "../shared/employee-import";
import { DEFAULT_MEAL_PRICE, parseMenuPrice } from "../shared/money";
import {
  ALL_WEEKDAYS,
  WEEKDAY_OPTIONS,
  availableDaysLabel,
  menuAvailableForDates,
  menuAvailableOnDate,
  normalizeAvailableDays,
} from "../shared/menu-availability";
import { formatKoreanMobile, isKoreanMobile } from "../shared/phone";
import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES, type Language } from "../shared/localization";
import { api, json } from "./api";
import { I18nProvider, useI18n } from "./i18n";
import { InstallDialog, isStandaloneApp, useInstallFlow, type InstallResult } from "./install";
import { Onboarding } from "./Onboarding";
import { AdminMeetingRooms, MeetingRooms } from "./MeetingRooms";
import {
  disablePushNotifications,
  enablePushNotifications,
  getPushNotificationState,
  syncPushSubscription,
  type PushNotificationState,
} from "./push";
import type { TranslationKey } from "./translations";
import type { User } from "./types";

type Menu = {
  id: string;
  name: string;
  emoji: string;
  price: number;
  active?: number;
  availableDays?: number[];
};
type Option = { id: string; name: string };
type Order = {
  id: string;
  date: string;
  menuId: string;
  menuName: string;
  emoji: string;
  locationId: string;
  locationName: string;
  consumptionStatus?: MealConsumptionStatus | null;
};
type Vacation = {
  id: string;
  startDate: string;
  endDate: string;
  note?: string;
};
type AnnouncementImage = { id: string; url: string; fileName: string };
type Announcement = {
  id: string;
  title: string;
  body: string;
  pinned: number;
  active?: number;
  createdAt: string;
  images?: AnnouncementImage[];
};
type Holiday = { date: string; name: string; isHoliday: boolean };
type AppData = {
  user: User;
  menus: Menu[];
  locations: Option[];
  organizations: Option[];
  orders: Order[];
  vacations: Vacation[];
  announcements: Announcement[];
  holidays: Holiday[];
};
type Tab =
  | "home"
  | "meals"
  | "rooms"
  | "vacation"
  | "profile"
  | "feedback"
  | "guide"
  | "history"
  | "notifications"
  | "password"
  | "language"
  | "install"
  | "notices"
  | "admin";
type AdminSection =
  | "dashboard"
  | "orders"
  | "consumption"
  | "users"
  | "rooms"
  | "menus"
  | "notices"
  | "feedback"
  | "auto-push"
  | "push"
  | "settlement";
type ClientRoute = {
  tab: Tab;
  adminSection: AdminSection;
  selectedNoticeId: string | null;
};
type MealView = "today" | "week" | "month";

const ADMIN_SECTIONS: AdminSection[] = [
  "dashboard",
  "orders",
  "consumption",
  "users",
  "rooms",
  "menus",
  "notices",
  "feedback",
  "auto-push",
  "push",
  "settlement",
];
const TAB_PATHS: Partial<Record<Tab, string>> = {
  home: "/",
  meals: "/meals",
  rooms: "/rooms",
  vacation: "/vacation",
  profile: "/profile",
  feedback: "/profile/feedback",
  guide: "/profile/guide",
  history: "/profile/history",
  notifications: "/profile/notifications",
  password: "/profile/password",
  language: "/profile/language",
  notices: "/notices",
};

function routeForTab(tab: Tab): ClientRoute {
  return {
    tab,
    adminSection: "dashboard",
    selectedNoticeId: null,
  };
}

function routePath(route: ClientRoute) {
  if (route.tab === "admin") return `/admin/${route.adminSection}`;
  if (route.tab === "notices" && route.selectedNoticeId) {
    return `/notices/${encodeURIComponent(route.selectedNoticeId)}`;
  }
  return TAB_PATHS[route.tab] ?? "/";
}

function parseClientRoute(pathname: string, role: User["role"]): ClientRoute {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (path === "/admin" || path.startsWith("/admin/")) {
    if (role !== "admin") return routeForTab("home");
    const candidate = path.split("/")[2] || "dashboard";
    const adminSection = ADMIN_SECTIONS.includes(candidate as AdminSection)
      ? candidate as AdminSection
      : "dashboard";
    return { tab: "admin", adminSection, selectedNoticeId: null };
  }
  if (path === "/notices") return routeForTab("notices");
  if (path.startsWith("/notices/")) {
    const encodedId = path.slice("/notices/".length);
    try {
      return {
        tab: "notices",
        adminSection: "dashboard",
        selectedNoticeId: decodeURIComponent(encodedId) || null,
      };
    } catch {
      return routeForTab("notices");
    }
  }
  const entry = Object.entries(TAB_PATHS).find(([, route]) => route === path);
  return routeForTab((entry?.[0] as Tab | undefined) ?? "home");
}

const kstToday = () =>
  new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
const longDate = (date: string) =>
  new Intl.DateTimeFormat("ko-KR", {
    month: "long",
    day: "numeric",
    weekday: "short",
    timeZone: "UTC",
  }).format(new Date(`${date}T00:00:00Z`));
const monthLabel = (date: string) =>
  `${date.slice(0, 4)}년 ${Number(date.slice(5, 7))}월`;
const historyDate = (date: string) =>
  `${date.slice(0, 4)}. ${date.slice(5, 7)}. ${date.slice(8)}.`;

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [guideAfterOnboarding, setGuideAfterOnboarding] = useState(false);
  const installFlow = useInstallFlow();

  useEffect(() => {
    api<User>("/api/auth/me")
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    if (!user) return;
    void syncPushSubscription().catch(() => undefined);
  }, [user?.id]);

  if (loading) return <Splash />;
  if (!user) return <Login onLogin={setUser} />;
  const language = user.language ?? "ko";
  const onboardingStep = user.onboardingStep ?? (user.mustChangePassword ? 1 : 7);
  const authenticatedUser = { ...user, language, onboardingStep };
  async function logoutFromOnboarding() {
    await api("/api/auth/logout", json("POST"));
    setUser(null);
  }
  return (
    <I18nProvider language={language}>
      {onboardingStep < 7 ? (
        <Onboarding
          user={authenticatedUser}
          onUser={setUser}
          canInstall={installFlow.canInstall}
          requestInstall={installFlow.requestInstall}
          onComplete={setGuideAfterOnboarding}
          onLogout={logoutFromOnboarding}
        />
      ) : (
        <MainApp
          user={authenticatedUser}
          onUser={setUser}
          initialTab={guideAfterOnboarding ? "guide" : "home"}
          guideExitToHome={guideAfterOnboarding}
          onGuideExit={() => setGuideAfterOnboarding(false)}
          requestInstall={installFlow.requestInstall}
        />
      )}
      <InstallDialog open={installFlow.instructionsOpen} onClose={installFlow.closeInstructions} />
    </I18nProvider>
  );
}

function Splash() {
  return (
    <div className="splash">
      <div className="splash-brand">
        <img src={logo} alt="" />
        <strong>닥터스</strong>
      </div>
      {isStandaloneApp() && <p className="splash-tagline">함께 쓰는 닥터스 그룹 업무 공간</p>}
    </div>
  );
}

function Brand({
  compact = false,
  onClick,
}: {
  compact?: boolean;
  onClick?: () => void;
}) {
  const content = (
    <>
      <img src={logo} alt={onClick ? "" : "닥터스"} />
      <strong>닥터스</strong>
    </>
  );
  return onClick ? (
    <button
      type="button"
      className={`brand brand-button ${compact ? "brand--compact" : ""}`}
      aria-label="닥터스 홈으로 이동"
      onClick={onClick}
    >
      {content}
    </button>
  ) : (
    <div className={`brand ${compact ? "brand--compact" : ""}`}>{content}</div>
  );
}

export function Login({ onLogin }: { onLogin: (user: User) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const form = new FormData(event.currentTarget as HTMLFormElement);
      onLogin(
        await api<User>("/api/auth/login", json("POST", {
          email: String(form.get("email") ?? email),
          password: String(form.get("password") ?? password),
        })),
      );
    } catch (problem) {
      setError((problem as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="auth-page auth-page--login">
      <section className="auth-card">
        <Brand />
        <div className="auth-copy">
          <span>오늘 점심도 간단하게</span>
          <h1>반가워요.</h1>
        </div>
        <form onSubmit={submit} className="form-stack">
          <label>
            이메일
            <input
              name="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="이메일 입력"
              required
            />
          </label>
          <label>
            비밀번호
            <input
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호 입력"
              required
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          <button className="primary-button" disabled={busy}>
            {busy ? "확인 중..." : "로그인"}
          </button>
        </form>
        <p className="auth-help">계정이 없다면 사내 관리자에게 알려 주세요.</p>
      </section>
    </main>
  );
}

function PasswordChange({
  user,
  onDone,
}: {
  user: User;
  onDone: (mealReminderEnabled: boolean) => void;
}) {
  const [currentPassword, setCurrent] = useState("1234");
  const [newPassword, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mealReminderEnabled, setMealReminderEnabled] = useState<
    boolean | null
  >(user.mealReminderEnabled ?? null);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (newPassword !== confirm)
      return setError("새 비밀번호가 서로 다릅니다.");
    if (mealReminderEnabled === null)
      return setError("식단 신청 안내 수신 여부를 선택해 주세요.");
    try {
      await api(
        "/api/auth/password",
        json("POST", {
          currentPassword,
          newPassword,
          mealReminderEnabled,
        }),
      );
      onDone(mealReminderEnabled);
    } catch (problem) {
      setError((problem as Error).message);
    }
  }
  return (
    <main className="auth-page auth-page--setup">
      <section className="auth-card">
        <Brand />
        <div className="auth-copy">
          <span>{user.name}님, 처음 오셨군요</span>
          <h1>비밀번호를 바꿔 주세요.</h1>
          <p>다음 로그인부터 새 비밀번호를 사용하면 됩니다.</p>
        </div>
        <form onSubmit={submit} className="form-stack">
          <label>
            현재 비밀번호
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </label>
          <label>
            새 비밀번호
            <input
              type="password"
              minLength={4}
              value={newPassword}
              onChange={(e) => setNext(e.target.value)}
              required
            />
          </label>
          <label>
            새 비밀번호 확인
            <input
              type="password"
              minLength={4}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </label>
          <fieldset className="consent-choice">
            <legend>식단 신청 안내를 받으시겠습니까?</legend>
            <p>
              오전 9시 20분까지 당일 식단을 신청하지 않으면 등록된
              휴대폰 번호로 한 번 알려드려요.
            </p>
            <div>
              <label>
                <input
                  type="radio"
                  name="meal-reminder-consent"
                  checked={mealReminderEnabled === true}
                  onChange={() => setMealReminderEnabled(true)}
                />
                받을게요
              </label>
              <label>
                <input
                  type="radio"
                  name="meal-reminder-consent"
                  checked={mealReminderEnabled === false}
                  onChange={() => setMealReminderEnabled(false)}
                />
                받지 않을게요
              </label>
            </div>
            <small>수신 여부는 내 정보에서 언제든 바꿀 수 있어요.</small>
          </fieldset>
          {error && <p className="form-error">{error}</p>}
          <button className="primary-button">변경하고 시작하기</button>
        </form>
      </section>
    </main>
  );
}

function MainApp({
  user,
  onUser,
  initialTab = "home",
  guideExitToHome = false,
  onGuideExit,
  requestInstall,
}: {
  user: User;
  onUser: (user: User | null) => void;
  initialTab?: Tab;
  guideExitToHome?: boolean;
  onGuideExit?: () => void;
  requestInstall: () => Promise<InstallResult>;
}) {
  const { t } = useI18n();
  const [route, setRoute] = useState<ClientRoute>(() =>
    initialTab === "home"
      ? parseClientRoute(window.location.pathname, user.role)
      : routeForTab(initialTab),
  );
  const { tab, adminSection, selectedNoticeId } = route;
  const [data, setData] = useState<AppData | null>(null);
  const [error, setError] = useState("");
  const today = kstToday();
  const navigate = useCallback((nextRoute: ClientRoute, replace = false) => {
    const nextPath = routePath(nextRoute);
    if (window.location.pathname !== nextPath) {
      window.history[replace ? "replaceState" : "pushState"](null, "", nextPath);
    }
    setRoute(nextRoute);
  }, []);
  const navigateTab = useCallback((nextTab: Tab, replace = false) => {
    navigate(routeForTab(nextTab), replace);
  }, [navigate]);
  const navigateAdmin = useCallback((section: AdminSection, replace = false) => {
    navigate({ tab: "admin", adminSection: section, selectedNoticeId: null }, replace);
  }, [navigate]);
  const navigateNotice = useCallback((id: string) => {
    navigate({ tab: "notices", adminSection: "dashboard", selectedNoticeId: id });
  }, [navigate]);
  const load = useCallback(async () => {
    setError("");
    const year = Number(today.slice(0, 4));
    try {
      setData(
        await api<AppData>(
          `/api/app?start=${year}-01-01&end=${year + 1}-12-31`,
        ),
      );
    } catch (problem) {
      setError((problem as Error).message);
    }
  }, [today]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const canonicalPath = routePath(route);
    if (window.location.pathname !== canonicalPath) {
      window.history.replaceState(null, "", canonicalPath);
    }
  }, []);
  useEffect(() => {
    function restoreRoute() {
      const nextRoute = parseClientRoute(window.location.pathname, user.role);
      const canonicalPath = routePath(nextRoute);
      if (window.location.pathname !== canonicalPath) {
        window.history.replaceState(null, "", canonicalPath);
      }
      setRoute(nextRoute);
    }
    window.addEventListener("popstate", restoreRoute);
    return () => window.removeEventListener("popstate", restoreRoute);
  }, [user.role]);
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    function openHome(event: MessageEvent) {
      if (event.data?.type !== "DOCTORSMEAL_OPEN_HOME") return;
      navigateTab("home", true);
    }
    navigator.serviceWorker.addEventListener("message", openHome);
    return () => navigator.serviceWorker.removeEventListener("message", openHome);
  }, [navigateTab]);
  async function logout() {
    await api("/api/auth/logout", json("POST"));
    window.history.replaceState(null, "", "/");
    onUser(null);
  }

  if (!data)
    return (
      <div className="splash">{error || t("common.loading")}</div>
    );
  return (
    <div className={`site ${tab === "admin" ? "site--admin" : ""}`}>
      <header className="topbar">
        <Brand compact onClick={() => navigateTab("home")} />
        <div className="topbar-actions">
          {user.role === "admin" && (
            <button
              className={`icon-label ${tab === "admin" ? "is-active" : ""}`}
              onClick={() => tab === "admin" ? navigateTab("home") : navigateAdmin("dashboard")}
            >
              <Settings size={18} /> {t("common.admin")}
            </button>
          )}
          <button
            className="icon-button"
            aria-label={t("common.logout")}
            onClick={logout}
          >
            <LogOut size={19} />
          </button>
        </div>
      </header>
      {error && <div className="toast-error">{error}</div>}
      {tab === "home" && (
        <HomePage
          data={data}
          today={today}
          goMeals={() => navigateTab("meals")}
          openNotice={navigateNotice}
          reload={load}
        />
      )}
      {tab === "notices" && (
        <NoticesPage
          notices={data.announcements}
          selectedId={selectedNoticeId}
          onSelect={navigateNotice}
          onBack={() => navigateTab("home")}
        />
      )}
      {tab === "meals" && <MealsPage data={data} today={today} reload={load} />}
      {tab === "rooms" && <MeetingRooms user={data.user} />}
      {tab === "vacation" && <VacationPage data={data} reload={load} />}
      {tab === "profile" && (
        <ProfilePage
          user={data.user}
          onGuide={() => navigateTab("guide")}
          onHistory={() => navigateTab("history")}
          onNotifications={() => navigateTab("notifications")}
          onPassword={() => navigateTab("password")}
          onLanguage={() => navigateTab("language")}
          onInstall={() => void requestInstall()}
          onFeedback={() => navigateTab("feedback")}
        />
      )}
      {tab === "feedback" && (
        <FeedbackPage onBack={() => navigateTab("profile")} />
      )}
      {tab === "guide" && (
        <GuidePage
          onboarding={guideExitToHome}
          onBack={() => {
            navigateTab(guideExitToHome ? "home" : "profile");
            onGuideExit?.();
          }}
        />
      )}
      {tab === "history" && (
        <MealHistoryPage
          orders={data.orders}
          onBack={() => navigateTab("profile")}
          onConsumptionSaved={load}
        />
      )}
      {tab === "notifications" && (
        <MealReminderSettingsPage
          user={data.user}
          onBack={() => navigateTab("profile")}
          onChange={load}
        />
      )}
      {tab === "password" && (
        <PasswordSettingsPage onBack={() => navigateTab("profile")} />
      )}
      {tab === "language" && (
        <LanguageSettingsPage
          user={user}
          onBack={() => navigateTab("profile")}
          onChange={(language) => {
            onUser({ ...user, language });
            setData((current) => current ? { ...current, user: { ...current.user, language } } : current);
          }}
        />
      )}
      {tab === "admin" && user.role === "admin" && (
        <AdminPage
          appData={data}
          onDataChange={load}
          section={adminSection}
          onSection={navigateAdmin}
        />
      )}
      {tab !== "admin" && <BottomNav tab={tab} onTab={navigateTab} />}
    </div>
  );
}

function BottomNav({ tab, onTab }: { tab: Tab; onTab: (tab: Tab) => void }) {
  const { t } = useI18n();
  const items: Array<[Tab, string, typeof Home]> = [
    ["home", t("nav.home"), Home],
    ["meals", t("nav.meals"), CalendarDays],
    ["rooms", t("nav.rooms"), Clock3],
    ["vacation", t("nav.vacation"), Palmtree],
    ["profile", t("nav.profile"), UserRound],
  ];
  return (
    <nav className="bottom-nav">
      {items.map(([value, label, Icon]) => (
        <button
          key={value}
          className={
            tab === value ||
            (tab === "notices" && value === "home") ||
            (["feedback", "guide", "history", "notifications", "password", "language", "install"].includes(
              tab,
            ) && value === "profile")
              ? "is-active"
              : ""
          }
          onClick={() => onTab(value)}
        >
          <Icon size={21} />
          <span>{label}</span>
        </button>
      ))}
    </nav>
  );
}

function NoticeCard({ notice, onClick }: { notice?: Announcement; onClick: (id: string) => void }) {
  const { t } = useI18n();
  if (!notice) return null;
  return (
    <button
      type="button"
      className="notice-card"
      aria-label={`${notice.title} ${t("notices.open")}`}
      onClick={() => onClick(notice.id)}
    >
      <div className="notice-card-heading">
        <span className="notice-icon">👩‍🍳</span>
        <div>
          <strong>{notice.title}</strong>
          <p>{notice.body}</p>
        </div>
        <ChevronRight size={18} />
      </div>
    </button>
  );
}

function NoticesPage({
  notices,
  selectedId,
  onSelect,
  onBack,
}: {
  notices: Announcement[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onBack: () => void;
}) {
  const { t } = useI18n();
  const selected = notices.find((notice) => notice.id === selectedId) ?? notices[0];
  const [preview, setPreview] = useState<{ image: AnnouncementImage; index: number } | null>(null);
  return (
    <main className="page notices-page">
      <div className="page-heading">
        <div>
          <span>{t("notices.eyebrow")}</span>
          <h1>{t("notices.title")}</h1>
        </div>
        <button type="button" className="icon-button" aria-label={t("notices.back")} onClick={onBack}>
          <ChevronLeft />
        </button>
      </div>
      {selected ? (
        <article className="panel notice-detail">
          <div className="notice-detail-title">
            <span className="notice-icon">👩‍🍳</span>
            <div>
              <h2>{selected.title}</h2>
              <time>{selected.createdAt.slice(0, 10).replaceAll("-", ". ")}.</time>
            </div>
          </div>
          <p>{selected.body}</p>
          {selected.images?.length ? (
            <div className="notice-detail-images">
              {selected.images.map((image, index) => (
                <button
                  type="button"
                  key={image.id}
                  aria-label={`${selected.title} ${t("notices.image")} ${index + 1} ${t("notices.enlarge")}`}
                  onClick={() => setPreview({ image, index })}
                >
                  <img src={image.url} alt={`${selected.title} ${t("notices.image")} ${index + 1}`} loading="lazy" />
                </button>
              ))}
            </div>
          ) : null}
        </article>
      ) : (
        <Empty text={t("notices.empty")} />
      )}
      {notices.length ? (
        <section className="notice-list-section">
          <div className="section-title"><h2>{t("notices.list")}</h2><span>{notices.length}</span></div>
          <div className="card-list">
            {notices.map((notice) => (
              <button
                type="button"
                key={notice.id}
                className={`list-card notice-list-button ${notice.id === selected?.id ? "is-active" : ""}`}
                aria-label={`${notice.title} ${t("notices.view")}`}
                onClick={() => onSelect(notice.id)}
              >
                <span className="notice-icon">👩‍🍳</span>
                <div><strong>{notice.title}</strong><p>{notice.body}</p></div>
                <ChevronRight size={18} />
              </button>
            ))}
          </div>
        </section>
      ) : null}
      {preview && selected ? (
        <div className="image-lightbox" role="dialog" aria-modal="true" aria-label={`${t("notices.image")} ${t("notices.enlarge")}`}>
          <button type="button" className="icon-button" aria-label={t("notices.closeImage")} onClick={() => setPreview(null)}><X /></button>
          <img src={preview.image.url} alt={`${selected.title} ${t("notices.image")} ${preview.index + 1} ${t("notices.enlarge")}`} />
        </div>
      ) : null}
    </main>
  );
}


function MealConsumptionCard({
  order,
  date,
  compact = false,
  onSaved,
}: {
  order?: Order;
  date: string;
  compact?: boolean;
  onSaved?: (status: MealConsumptionStatus) => void;
}) {
  const { t } = useI18n();
  const [status, setStatus] = useState<MealConsumptionStatus | null>(order?.consumptionStatus ?? null);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setStatus(order?.consumptionStatus ?? null);
  }, [order?.id, order?.consumptionStatus]);
  const visible = Boolean(
    order &&
    isActualMealOrder(order) &&
    canReportMealConsumption(date),
  );
  async function save(next: MealConsumptionStatus) {
    if (!visible || saving) return;
    setSaving(true);
    setMessage("");
    try {
      await api("/api/orders/consumption", json("POST", { date, status: next }));
      setStatus(next);
      setMessage(next === "eaten" ? t(compact ? "history.consumptionEatenSaved" : "home.consumptionEatenSaved") : t(compact ? "history.consumptionUneatenSaved" : "home.consumptionUneatenSaved"));
      onSaved?.(next);
    } catch (problem) {
      setMessage((problem as Error).message);
    } finally {
      setSaving(false);
    }
  }
  if (!visible || !order) return null;
  return (
    <section className={`panel consumption-card${compact ? " consumption-card--compact" : ""}`}>
      {compact ? null : <h2>{t("home.consumptionTitle")}</h2>}
      <p>{t(compact ? "history.consumptionPrompt" : "home.consumptionDescription")}</p>
      <div className="binary-choices">
        <button
          type="button"
          className={status === "eaten" ? "is-selected" : ""}
          onClick={() => void save("eaten")}
        >
          {t("home.consumptionEaten")}
        </button>
        <button
          type="button"
          className={status === "uneaten" ? "is-selected" : ""}
          onClick={() => void save("uneaten")}
        >
          {t("home.consumptionUneaten")}
        </button>
      </div>
      {compact ? null : <p className="subtle-note">{t("home.consumptionChange")}</p>}
      {message && <p className="form-message">{message}</p>}
    </section>
  );
}

function HomePage({
  data,
  today,
  goMeals,
  openNotice,
  reload,
}: {
  data: AppData;
  today: string;
  goMeals: () => void;
  openNotice: (id: string) => void;
  reload: () => Promise<void>;
}) {
  const { t, formatDate } = useI18n();
  const [selectedDate, setSelectedDate] = useState(today);
  const order = data.orders.find((item) => item.date === selectedDate);
  const vacation = data.vacations.some(
    (item) => item.startDate <= selectedDate && item.endDate >= selectedDate,
  );
  const editable = !isSunday(selectedDate) && canEditMealDate(selectedDate);
  const weekStart = startOfWeekMonday(today);
  const week = Array.from({ length: 7 }, (_, index) =>
    addDays(weekStart, index),
  );
  const [menuId, setMenu] = useState(order?.menuId || "");
  const [locationId, setLocation] = useState(
    order?.locationId ||
      data.user.defaultLocationId ||
      data.locations[0]?.id ||
      "",
  );
  const [message, setMessage] = useState("");
  const selectedMenu = data.menus.find((menu) => menu.id === menuId);
  const availableMenus = data.menus.filter((menu) =>
    menuAvailableOnDate(menu.availableDays, selectedDate),
  );
  const selectedLocation = data.locations.find(
    (location) => location.id === locationId,
  );
  const hasChanges = Boolean(
    order && (order.menuId !== menuId || order.locationId !== locationId),
  );
  useEffect(() => {
    setMenu(order?.menuId || "");
    setLocation(
      order?.locationId ||
        data.user.defaultLocationId ||
        data.locations[0]?.id ||
        "",
    );
    setMessage("");
  }, [
    selectedDate,
    order?.menuId,
    order?.locationId,
    data.user.defaultLocationId,
    data.locations,
  ]);
  async function save() {
    if (isSunday(selectedDate)) return setMessage(t("meals.sundayClosed"));
    if (!editable) return setMessage(t("home.closed"));
    if (!menuId) return setMessage(t("home.chooseMenu"));
    try {
      await api(
        "/api/orders/bulk",
        json("POST", {
          dates: [selectedDate],
          menuId,
          locationId,
          action: "upsert",
        }),
      );
      setMessage(order ? t("home.changed") : t("home.saved"));
      await reload();
    } catch (problem) {
      setMessage((problem as Error).message);
    }
  }
  const status = vacation
    ? { label: t("home.statusVacation"), tone: "muted" }
    : order
      ? { label: t("home.statusDone"), tone: "success" }
      : { label: t("home.statusPending"), tone: "danger" };
  return (
    <main className="page home-page">
      <section className="hero">
        <p>{data.user.name} · {t("home.greeting")}</p>
        <h1>
          {isSunday(selectedDate)
            ? t("meals.sundayClosed")
            : !editable
            ? selectedDate === today
              ? t("home.closedToday")
              : t("home.closedDate", { day: Number(selectedDate.slice(8)) })
            : selectedDate === today
              ? t("home.questionToday")
              : t("home.questionDate", { day: Number(selectedDate.slice(8)) })}
        </h1>
      </section>
      <NoticeCard notice={data.announcements[0]} onClick={openNotice} />
      {selectedDate === today && !vacation ? (
        <MealConsumptionCard order={order} date={today} onSaved={() => void reload()} />
      ) : null}
      <section className="week-strip">
        <div className="section-title">
          <h2>{formatDate(`${today}T00:00:00Z`, { year: "numeric", month: "long", timeZone: "UTC" })}</h2>
          <button onClick={goMeals}>{t("home.weekly")}</button>
        </div>
        <div className="week-days">
          {week.map((date) => {
            const sunday = isSunday(date);
            const isLocked = sunday || !canEditMealDate(date);
            return (
              <button
                type="button"
                key={date}
                aria-pressed={date === selectedDate}
                title={sunday ? t("meals.sundayClosed") : isLocked ? t("home.closed") : undefined}
                onClick={() => setSelectedDate(date)}
                className={`${date === today ? "is-today" : ""} ${date === selectedDate ? "is-selected" : ""} ${isLocked ? "is-locked" : ""} ${isWeekend(date) ? "is-offday" : "is-weekday"}`}
              >
                <span>{t(`weekday.${new Date(`${date}T00:00:00Z`).getUTCDay()}.short` as TranslationKey)}</span>
                <strong>{Number(date.slice(-2))}</strong>
              </button>
            );
          })}
        </div>
      </section>
      <section className="meal-card">
        <div className="meal-card-head">
          <span className={`status-pill status-${status.tone}`}>
            {status.label}
          </span>
          <span>{t("home.cutoff")}</span>
        </div>
        <div className="location-line">
          <div>
            <small>{t("home.location")}</small>
            <strong>
              {data.locations.find((item) => item.id === locationId)?.name ||
                t("common.unassigned")}
            </strong>
          </div>
          <select
            aria-label={t("home.changeLocation")}
            value={locationId}
            disabled={vacation || !editable}
            onChange={(e) => setLocation(e.target.value)}
          >
            {data.locations.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
        <div className="menu-grid">
          {availableMenus.map((menu) => (
            <button
              key={menu.id}
              disabled={vacation || !editable}
              className={menuId === menu.id ? "is-selected" : ""}
              onClick={() => setMenu(menu.id)}
            >
              <span>{menu.emoji}</span>
              <strong>{menu.name}</strong>
            </button>
          ))}
        </div>
        {hasChanges && selectedMenu && selectedLocation ? (
          <div className="meal-change-preview" role="status">
            <span>
              {t("home.current")} {order?.emoji} {order?.menuName} · {order?.locationName}
            </span>
            <strong>
              {t("home.afterChange")} {selectedMenu.emoji} {selectedMenu.name} ·{" "}
              {selectedLocation.name}
            </strong>
          </div>
        ) : null}
        <button
          className="primary-button"
          disabled={vacation || !editable || Boolean(order && !hasChanges)}
          onClick={save}
        >
          {vacation
            ? t("home.registeredVacation")
            : !editable
              ? t("home.closed")
              : order
                ? hasChanges
                  ? t("home.change")
                  : t("home.unchanged")
                : menuId
                  ? t("home.applyMenu", { menu: selectedMenu?.name ?? "" })
                  : t("home.chooseMenu")}
        </button>
        {message && <p className="form-message">{message}</p>}
      </section>
    </main>
  );
}

function MealsPage({
  data,
  today,
  reload,
}: {
  data: AppData;
  today: string;
  reload: () => Promise<void>;
}) {
  const { t, formatDate } = useI18n();
  const [view, setView] = useState<MealView>("week");
  const [cursor, setCursor] = useState(today);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [menuId, setMenuId] = useState(
    data.menus.find((menu) => menu.name === "백반")?.id ||
      data.menus[0]?.id ||
      "",
  );
  const [locationId, setLocationId] = useState(
    data.user.defaultLocationId || data.locations[0]?.id || "",
  );
  const [excludeWeekends, setExcludeWeekends] = useState(true);
  const [excludeHolidays, setExcludeHolidays] = useState(true);
  const [message, setMessage] = useState("");
  const holidaySet = useMemo(
    () =>
      new Set(
        data.holidays.filter((item) => item.isHoliday).map((item) => item.date),
      ),
    [data.holidays],
  );
  const weekStart = startOfWeekMonday(cursor);
  const weekDates = Array.from({ length: 7 }, (_, index) =>
    addDays(weekStart, index),
  );
  const monthGrid = buildMonthGrid(
    Number(cursor.slice(0, 4)),
    Number(cursor.slice(5, 7)),
  );
  const visibleDates =
    view === "today"
      ? [cursor]
      : view === "week"
        ? weekDates
        : monthGrid.filter((day) => day.inMonth).map((day) => day.date);
  const selectableVisibleDates = filterBulkDates(
    visibleDates.filter((date) => canEditMealDate(date)),
    { excludeWeekends, excludeHolidays, holidays: holidaySet },
  );
  const effectiveSelectedDates = filterBulkDates(
    selectedDates.filter((date) => canEditMealDate(date)),
    { excludeWeekends, excludeHolidays, holidays: holidaySet },
  );
  const availableMenus = data.menus.filter(
    (menu) =>
      effectiveSelectedDates.length === 0 ||
      menuAvailableForDates(menu.availableDays, effectiveSelectedDates),
  );
  useEffect(() => {
    if (menuId && !availableMenus.some((menu) => menu.id === menuId)) {
      setMenuId(
        availableMenus.find((menu) => menu.name === "백반")?.id ||
          availableMenus[0]?.id ||
          "",
      );
    }
  }, [availableMenus, menuId]);
  const allVisibleDatesSelected =
    selectableVisibleDates.length > 0 &&
    selectableVisibleDates.every((date) => selectedDates.includes(date));
  function move(amount: number) {
    if (view === "today") setCursor(addDays(cursor, amount));
    else if (view === "week") setCursor(addDays(cursor, amount * 7));
    else {
      const value = new Date(`${cursor.slice(0, 7)}-15T00:00:00Z`);
      value.setUTCMonth(value.getUTCMonth() + amount);
      setCursor(value.toISOString().slice(0, 10));
    }
    setSelectedDates([]);
  }
  function toggle(date: string) {
    if (isSunday(date)) return setMessage(t("meals.sundayClosed"));
    if (!canEditMealDate(date))
      return setMessage(t("home.closed"));
    setSelectedDates((current) =>
      current.includes(date)
        ? current.filter((item) => item !== date)
        : [...current, date],
    );
  }
  function toggleVisibleDates() {
    if (allVisibleDatesSelected) {
      const visibleSet = new Set(visibleDates);
      setSelectedDates((current) =>
        current.filter((date) => !visibleSet.has(date)),
      );
      return;
    }
    setSelectedDates((current) => [
      ...new Set([...current, ...selectableVisibleDates]),
    ]);
  }
  async function apply(action: "upsert" | "delete") {
    const dates = filterBulkDates(
      selectedDates.filter((date) => canEditMealDate(date)),
      { excludeWeekends, excludeHolidays, holidays: holidaySet },
    );
    if (!dates.length) return setMessage(t("meals.selectDates"));
    if (action === "upsert" && !menuId)
      return setMessage(t("meals.noMenu"));
    try {
      await api(
        "/api/orders/bulk",
        json("POST", { dates, menuId, locationId, action }),
      );
      setMessage(action === "delete" ? t("meals.delete") : t("home.changed"));
      setSelectedDates([]);
      await reload();
    } catch (problem) {
      setMessage((problem as Error).message);
    }
  }
  const dateState = (date: string) => {
    if (
      data.vacations.some(
        (item) => item.startDate <= date && item.endDate >= date,
      )
    )
      return { label: t("meals.vacation"), className: "vacation" };
    const order = data.orders.find((item) => item.date === date);
    return order
      ? { label: `${order.emoji} ${order.menuName}`, className: "ordered" }
      : { label: t("home.statusPending"), className: "pending" };
  };
  return (
    <main className="page meals-page">
      <div className="page-heading">
        <div>
          <span>{t("meals.eyebrow")}</span>
          <h1>{t("meals.title")}</h1>
        </div>
        <Utensils />
      </div>
      <div className="segmented">
        {(["today", "week", "month"] as MealView[]).map((item) => (
          <button
            key={item}
            className={view === item ? "is-active" : ""}
            onClick={() => {
              setView(item);
              setSelectedDates([]);
            }}
          >
            {item === "today" ? t("meals.today") : item === "week" ? t("meals.week") : t("meals.month")}
          </button>
        ))}
      </div>
      <div className="date-controller">
        <button onClick={() => move(-1)}>
          <ChevronLeft />
        </button>
        <strong>
          {view === "today"
            ? formatDate(`${cursor}T00:00:00Z`, { month: "long", day: "numeric", weekday: "short", timeZone: "UTC" })
            : view === "week"
              ? `${formatDate(`${weekStart}T00:00:00Z`, { month: "short", day: "numeric", timeZone: "UTC" })} – ${formatDate(`${weekDates[6]}T00:00:00Z`, { month: "short", day: "numeric", timeZone: "UTC" })}`
              : formatDate(`${cursor}T00:00:00Z`, { year: "numeric", month: "long", timeZone: "UTC" })}
        </strong>
        <button onClick={() => move(1)}>
          <ChevronRight />
        </button>
      </div>
      <div className="bulk-options">
        <label>
          <input
            type="checkbox"
            checked={excludeWeekends}
            onChange={(e) => setExcludeWeekends(e.target.checked)}
          />{" "}
          {t("meals.excludeWeekends")}
        </label>
        <label>
          <input
            type="checkbox"
            checked={excludeHolidays}
            onChange={(e) => setExcludeHolidays(e.target.checked)}
          />{" "}
          {t("meals.excludeHolidays")}
        </label>
        <button
          type="button"
          aria-pressed={allVisibleDatesSelected}
          onClick={toggleVisibleDates}
        >
          {allVisibleDatesSelected ? t("meals.clearVisible") : t("meals.selectVisible")}
        </button>
      </div>
      {view === "month" ? (
        <MonthCalendar
          days={monthGrid}
          selected={selectedDates}
          holidays={data.holidays}
          state={dateState}
          onToggle={toggle}
        />
      ) : (
        <div className="day-list">
          {visibleDates.map((date) => {
            const state = dateState(date);
            const holiday = data.holidays.find(
              (item) => item.date === date && item.isHoliday,
            );
            const sunday = isSunday(date);
            const editable = !sunday && canEditMealDate(date);
            const statusLabel = sunday ? t("meals.sundayClosed") : state.label;
            const statusClass = sunday ? "vacation" : state.className;
            return (
              <button
                key={date}
                disabled={!editable}
                className={`day-row ${selectedDates.includes(date) ? "is-selected" : ""} ${!editable ? "is-locked" : ""} ${holiday || isWeekend(date) ? "is-offday" : "is-weekday"}`}
                onClick={() => toggle(date)}
              >
                <span className="date-box">
                  <small>
                    {
                      t(`weekday.${new Date(`${date}T00:00:00Z`).getUTCDay()}.short` as TranslationKey)
                    }
                  </small>
                  <strong>{Number(date.slice(-2))}</strong>
                </span>
                <span className="day-status">
                  <strong className={`text-${statusClass}`}>
                    {statusLabel}
                  </strong>
                  <small>
                    {sunday
                      ? holiday?.name || ""
                      : holiday?.name ||
                        (!editable
                          ? t("meals.closed")
                          : data.orders.find((item) => item.date === date)
                              ?.locationName || data.user.defaultLocationName)}
                  </small>
                </span>
                {selectedDates.includes(date) ? <Check /> : <ChevronRight />}
              </button>
            );
          })}
        </div>
      )}
      <section className="selection-panel">
        <div className="selection-summary">
          <small>{t("meals.selectDates")}</small>
          <strong>{t("meals.selectedCount", { count: selectedDates.length })}</strong>
        </div>
        <div className="selection-fields">
          <label>
            {t("meals.menu")}
            <select value={menuId} onChange={(e) => setMenuId(e.target.value)}>
              {!availableMenus.length && (
                <option value="">{t("meals.noMenu")}</option>
              )}
              {availableMenus.map((menu) => (
                <option key={menu.id} value={menu.id}>
                  {menu.emoji} {menu.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t("meals.location")}
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              {data.locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="selection-actions">
          <button
            className="danger-button"
            disabled={!selectedDates.length}
            onClick={() => apply("delete")}
          >
            <Trash2 size={17} /> {t("meals.delete")}
          </button>
          <button
            className="primary-button"
            disabled={!selectedDates.length}
            onClick={() => apply("upsert")}
          >
            <Save size={17} /> {t("meals.applyBulk")}
          </button>
        </div>
        {message && <p className="form-message">{message}</p>}
      </section>
    </main>
  );
}

function MonthCalendar({
  days,
  selected,
  holidays,
  state,
  onToggle,
}: {
  days: ReturnType<typeof buildMonthGrid>;
  selected: string[];
  holidays: Holiday[];
  state: (date: string) => { label: string; className: string };
  onToggle: (date: string) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="calendar">
      <div className="calendar-weekdays">
        {[1, 2, 3, 4, 5, 6, 0].map((day) => (
          <span key={day}>{t(`weekday.${day}.short` as TranslationKey)}</span>
        ))}
      </div>
      <div className="calendar-grid">
        {days.map((day) => {
          const status = state(day.date);
          const holiday = holidays.find(
            (item) => item.date === day.date && item.isHoliday,
          );
          const sunday = isSunday(day.date);
          const offday = Boolean(holiday) || isWeekend(day.date);
          const editable = day.inMonth && !sunday && canEditMealDate(day.date);
          return (
            <button
              key={day.date}
              disabled={!day.inMonth || !editable}
              onClick={() => onToggle(day.date)}
              className={`${day.inMonth ? "" : "outside"} ${selected.includes(day.date) ? "is-selected" : ""} ${status.className === "pending" && !offday ? "is-pending" : ""} ${!editable ? "is-locked" : ""} ${offday ? "is-offday" : "is-weekday"}`}
            >
              <span>{day.day}</span>
              {holiday ? (
                <small className="holiday-dot" title={holiday.name}>
                  휴
                </small>
              ) : status.className === "ordered" ? (
                <small>{status.label.split(" ")[0]}</small>
              ) : status.className === "vacation" ? (
                <small>{t("meals.vacation")}</small>
              ) : (
                <i />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function VacationPage({
  data,
  reload,
}: {
  data: AppData;
  reload: () => Promise<void>;
}) {
  const { t, formatDate } = useI18n();
  const today = kstToday();
  const [startDate, setStart] = useState(today);
  const [endDate, setEnd] = useState(today);
  const [message, setMessage] = useState("");
  async function save(event: FormEvent) {
    event.preventDefault();
    try {
      await api("/api/vacations", json("POST", { startDate, endDate }));
      setMessage(t("vacation.description"));
      await reload();
    } catch (problem) {
      setMessage((problem as Error).message);
    }
  }
  async function remove(id: string) {
    await api(`/api/vacations/${id}`, json("DELETE"));
    await reload();
  }
  return (
    <main className="page">
      <div className="page-heading">
        <div>
          <span>{t("vacation.eyebrow")}</span>
          <h1>{t("vacation.title")}</h1>
        </div>
        <Palmtree />
      </div>
      <div className="vacation-note">
        <Check size={18} />
        <p>{t("vacation.description")}</p>
      </div>
      <form className="panel form-stack" onSubmit={save}>
        <div className="field-row">
          <label>
            {t("vacation.start")}
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStart(e.target.value)}
            />
          </label>
          <label>
            {t("vacation.end")}
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEnd(e.target.value)}
            />
          </label>
        </div>
        <button className="primary-button">
          <Plus size={18} /> {t("vacation.register")}
        </button>
        {message && <p className="form-message">{message}</p>}
      </form>
      <section className="section-block">
        <div className="section-title">
          <h2>{t("vacation.registered")}</h2>
          <span>{data.vacations.length}</span>
        </div>
        {data.vacations.length ? (
          <div className="card-list">
            {data.vacations.map((item) => (
              <article className="list-card" key={item.id}>
                <span className="list-icon">
                  <Palmtree />
                </span>
                <div>
                  <strong>
                    {item.startDate === item.endDate
                      ? formatDate(`${item.startDate}T00:00:00Z`, { month: "long", day: "numeric", weekday: "short", timeZone: "UTC" })
                      : `${item.startDate} – ${item.endDate}`}
                  </strong>
                  <p>{item.note || t("vacation.excluded")}</p>
                </div>
                <button
                  className="icon-button"
                  onClick={() => remove(item.id)}
                  aria-label={t("vacation.cancel")}
                >
                  <X />
                </button>
              </article>
            ))}
          </div>
        ) : (
          <Empty text={t("vacation.empty")} />
        )}
      </section>
    </main>
  );
}

function ProfilePage({
  user,
  onGuide,
  onHistory,
  onNotifications,
  onPassword,
  onLanguage,
  onInstall,
  onFeedback,
}: {
  user: User;
  onGuide: () => void;
  onHistory: () => void;
  onNotifications: () => void;
  onPassword: () => void;
  onLanguage: () => void;
  onInstall: () => void;
  onFeedback: () => void;
}) {
  const { t, language } = useI18n();
  return (
    <main className="page">
      <div className="page-heading">
        <div>
          <span>{t("profile.eyebrow")}</span>
          <h1>{language === "ko" ? `${user.name}님` : user.name}</h1>
        </div>
        <button type="button" className="icon-label profile-feedback-button" onClick={onFeedback}>
          {t("profile.feedback")}
        </button>
      </div>
      <section className="panel profile-card">
        <h2>{user.name}</h2>
        <p>{user.email}</p>
        <dl>
          <div>
            <dt>{t("profile.organization")}</dt>
            <dd>{user.organizationName || t("common.unassigned")}</dd>
          </div>
          <div>
            <dt>{t("profile.location")}</dt>
            <dd>{user.defaultLocationName || t("common.unassigned")}</dd>
          </div>
          <div>
            <dt>{t("profile.phone")}</dt>
            <dd>{user.phone || t("common.unregistered")}</dd>
          </div>
        </dl>
        <p className="subtle-note">{t("profile.notice")}</p>
      </section>
      <section className="panel">
        <button type="button" className="action-row" onClick={onGuide}>
          <span className="list-icon">
            <CircleHelp />
          </span>
          <div>
            <strong>{t("profile.guide.title")}</strong>
            <p>{t("profile.guide.description")}</p>
          </div>
          <ChevronRight />
        </button>
        <button type="button" className="action-row" onClick={onHistory}>
          <span className="list-icon">
            <CalendarDays />
          </span>
          <div>
            <strong>{t("profile.history.title")}</strong>
            <p>{t("profile.history.description")}</p>
          </div>
          <ChevronRight />
        </button>
        <button
          type="button"
          className="action-row"
          onClick={onNotifications}
        >
          <span className="list-icon">
            <Bell />
          </span>
          <div>
            <strong>{t("profile.reminder.title")}</strong>
            <p>
              {user.mealReminderEnabled === false
                ? t("profile.reminder.disabled")
                : t("profile.reminder.enabled")}
            </p>
          </div>
          <ChevronRight />
        </button>
        <button type="button" className="action-row" onClick={onPassword}>
          <span className="list-icon">
            <KeyRound />
          </span>
          <div>
            <strong>{t("profile.password.title")}</strong>
            <p>{t("profile.password.description")}</p>
          </div>
          <ChevronRight />
        </button>
        <button type="button" className="action-row" onClick={onLanguage}>
          <span className="list-icon">
            <Languages />
          </span>
          <div>
            <strong>{t("profile.language.title")}</strong>
            <p>{t("profile.language.description")}</p>
          </div>
          <ChevronRight />
        </button>
        <button type="button" className="action-row" onClick={onInstall}>
          <span className="list-icon">
            <Download />
          </span>
          <div>
            <strong>{t("profile.install.title")}</strong>
            <p>{t("profile.install.description")}</p>
          </div>
          <ChevronRight />
        </button>
      </section>
    </main>
  );
}

const FEEDBACK_MAX_PHOTOS = 5;
const FEEDBACK_MAX_PHOTO_BYTES = 10 * 1024 * 1024;

type FeedbackPhotoDraft = { id: string; file: File; url: string };

function FeedbackPage({ onBack }: { onBack: () => void }) {
  const { t, formatNumber, errorMessage } = useI18n();
  const [content, setContent] = useState("");
  const [photos, setPhotos] = useState<FeedbackPhotoDraft[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const photosRef = useRef(photos);
  photosRef.current = photos;

  useEffect(() => () => {
    photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.url));
  }, []);

  function resetSubmitted() {
    setSubmitted(false);
    setError("");
  }

  function addPhotos(event: ChangeEvent<HTMLInputElement>) {
    const incoming = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!incoming.length) return;
    if (incoming.some((file) => !file.type.startsWith("image/") || file.size > FEEDBACK_MAX_PHOTO_BYTES)) {
      setError(t("feedback.photoInvalid"));
      return;
    }
    if (photos.length + incoming.length > FEEDBACK_MAX_PHOTOS) {
      setError(t("feedback.photoLimit"));
      return;
    }
    resetSubmitted();
    setPhotos((current) => [
      ...current,
      ...incoming.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${crypto.randomUUID()}`,
        file,
        url: URL.createObjectURL(file),
      })),
    ]);
  }

  function removePhoto(id: string) {
    resetSubmitted();
    setPhotos((current) => {
      const target = current.find((photo) => photo.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return current.filter((photo) => photo.id !== id);
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!content.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.set("content", content.trim());
      photos.forEach((photo) => form.append("images", photo.file));
      await api("/api/feedback", { method: "POST", body: form });
      setSubmitted(true);
    } catch (problem) {
      setError(errorMessage(problem));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page feedback-page">
      <div className="page-heading">
        <div>
          <span>{t("feedback.eyebrow")}</span>
          <h1>{t("feedback.title")}</h1>
        </div>
        <button type="button" className="icon-button" aria-label={t("common.back")} onClick={onBack}>
          <ChevronLeft />
        </button>
      </div>
      <form className="feedback-form" onSubmit={(event) => void submit(event)}>
        <section className="panel feedback-panel">
          <p>{t("feedback.description")}</p>
          <label>
            <span>{t("feedback.content")}</span>
            <textarea
              maxLength={1000}
              value={content}
              placeholder={t("feedback.placeholder")}
              onChange={(event) => {
                setContent(event.target.value);
                resetSubmitted();
              }}
            />
          </label>
          <p className="feedback-count">{formatNumber(content.length)} / {formatNumber(1000)}</p>
          <label className="image-upload">
            <span>
              <ImagePlus size={19} /> {t("feedback.photos")}
            </span>
            <input
              aria-label={t("feedback.photos")}
              type="file"
              accept="image/*"
              multiple
              onChange={addPhotos}
            />
            <small>
              {photos.length
                ? t("feedback.photosCount", { count: photos.length, max: FEEDBACK_MAX_PHOTOS })
                : t("feedback.photosHint")}
            </small>
          </label>
          {photos.length ? (
            <ul className="feedback-photo-grid">
              {photos.map((photo, index) => (
                <li key={photo.id}>
                  <img src={photo.url} alt={t("feedback.photoPreview", { index: index + 1 })} />
                  <button
                    type="button"
                    className="icon-button"
                    aria-label={t("feedback.photoRemove", { name: photo.file.name })}
                    onClick={() => removePhoto(photo.id)}
                  >
                    <X size={16} />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {error ? <p className="form-message is-error" role="alert">{error}</p> : null}
          {submitted && (
            <p className="form-message" role="status">
              {t("feedback.thanks")}
            </p>
          )}
        </section>
        <div className="feedback-submit-bar">
          <button className="primary-button" disabled={!content.trim() || busy}>
            {busy ? t("feedback.sending") : t("feedback.submit")}
          </button>
        </div>
      </form>
    </main>
  );
}

function InstallAppPage({
  onBack,
}: {
  onBack: () => void;
}) {
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone);

  return (
    <main className="page install-page">
      <div className="page-heading page-heading--with-back">
        <button className="icon-button" type="button" onClick={onBack} aria-label="내 정보로 돌아가기">
          <ChevronLeft />
        </button>
        <div>
          <span>앱 설치</span>
          <h1>홈 화면에서 바로 열어요.</h1>
        </div>
      </div>
      <section className="panel install-card">
        <div className="install-app-icon">
          <img src={logo} alt="" />
        </div>
        <div>
          <h2>닥터스</h2>
          <p>브라우저를 찾지 않아도 홈 화면에서 앱처럼 사용할 수 있어요.</p>
        </div>
      </section>
      {isStandalone ? (
        <section className="panel install-status">
          <Check />
          <div>
            <h2>이미 설치되어 있어요.</h2>
            <p>홈 화면의 닥터스 아이콘으로 실행해 주세요.</p>
          </div>
        </section>
      ) : isIos ? (
        <section className="panel install-instructions">
          <h2>아이폰에 설치하기</h2>
          <ol className="install-steps">
            <li><span>1</span><p>Safari의 공유 버튼을 누르세요.</p></li>
            <li><span>2</span><p>메뉴에서 홈 화면에 추가를 선택한 뒤 추가를 눌러 주세요.</p></li>
          </ol>
          <p className="subtle-note">Chrome이 아니라 Safari에서 열어 주세요.</p>
        </section>
      ) : (
        <section className="panel install-instructions">
          <h2>브라우저 메뉴에서 설치해 주세요.</h2>
          <p>Chrome 또는 Edge 메뉴에서 ‘앱 설치’나 ‘홈 화면에 추가’를 선택해 주세요.</p>
        </section>
      )}
    </main>
  );
}

function LanguageSettingsPage({
  user,
  onBack,
  onChange,
}: {
  user: User;
  onBack: () => void;
  onChange: (language: Language) => void;
}) {
  const { t, errorMessage } = useI18n();
  const [language, setLanguage] = useState<Language>(user.language ?? "ko");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    setError("");
    try {
      const result = await api<{ language: Language }>(
        "/api/profile/language",
        json("PUT", { language }),
      );
      onChange(result.language);
      setMessage(t("language.saved"));
    } catch (problem) {
      setError(errorMessage(problem));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page">
      <div className="page-heading">
        <div><span>{t("language.eyebrow")}</span><h1>{t("language.title")}</h1></div>
        <button type="button" className="icon-button" aria-label={t("common.back")} onClick={onBack}><ChevronLeft /></button>
      </div>
      <form className="panel form-stack" onSubmit={save}>
        <p>{t("language.description")}</p>
        <div className="language-choices" role="radiogroup" aria-label={t("language.eyebrow")}>
          {SUPPORTED_LANGUAGES.map((value) => (
            <label key={value} className={language === value ? "is-selected" : ""}>
              <input type="radio" name="profile-language" value={value} checked={language === value} onChange={() => setLanguage(value)} />
              <span className="language-symbol">{value === "ko" ? "한" : value === "en" ? "EN" : value === "zh-CN" ? "简" : "繁"}</span>
              <span><strong>{LANGUAGE_LABELS[value].native}</strong><small>{LANGUAGE_LABELS[value].korean}</small></span>
              <Check className="choice-check" />
            </label>
          ))}
        </div>
        {error && <p className="form-error">{error}</p>}
        {message && <p className="form-message">{message}</p>}
        <button className="primary-button" disabled={busy}>{busy ? t("common.saving") : t("common.save")}</button>
      </form>
    </main>
  );
}

function MealReminderSettingsPage({
  user,
  onBack,
  onChange,
}: {
  user: User;
  onBack: () => void;
  onChange: () => Promise<void>;
}) {
  const { t, errorMessage } = useI18n();
  const [enabled, setEnabled] = useState(user.mealReminderEnabled !== false);
  const [workDays, setWorkDays] = useState<number[]>(
    user.workDays?.length ? user.workDays : [1, 2, 3, 4, 5],
  );
  const [message, setMessage] = useState("");
  const [workDaysMessage, setWorkDaysMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pushState, setPushState] = useState<PushNotificationState>("disabled");
  const [pushError, setPushError] = useState("");
  useEffect(() => {
    void getPushNotificationState().then(setPushState).catch((problem) => setPushError(errorMessage(problem)));
  }, []);
  async function togglePush() {
    setPushError("");
    setBusy(true);
    try {
      setPushState(pushState === "enabled"
        ? await disablePushNotifications()
        : await enablePushNotifications());
    } catch (problem) {
      setPushError(errorMessage(problem));
    } finally {
      setBusy(false);
    }
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    setMessage("");
    setError("");
    setBusy(true);
    try {
      await api(
        "/api/profile/meal-reminder",
        json("PUT", { enabled }),
      );
      setMessage(
        enabled
          ? t("reminder.enabledSaved")
          : t("reminder.disabledSaved"),
      );
      await onChange();
    } catch (problem) {
      setError(errorMessage(problem));
    } finally {
      setBusy(false);
    }
  }
  function toggleWorkDay(day: number) {
    setWorkDays((current) =>
      current.includes(day)
        ? current.filter((value) => value !== day)
        : [...current, day],
    );
  }
  async function saveWorkDays(event: FormEvent) {
    event.preventDefault();
    setWorkDaysMessage("");
    setError("");
    if (!workDays.length) {
      setError(t("error.INVALID_WORK_DAYS"));
      return;
    }
    setBusy(true);
    try {
      await api(
        "/api/profile/work-days",
        json("PUT", { workDays: normalizeAvailableDays(workDays) }),
      );
      setWorkDaysMessage(t("reminder.workdaysSaved"));
      await onChange();
    } catch (problem) {
      setError(errorMessage(problem));
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="page">
      <div className="page-heading">
        <div>
          <span>{t("reminder.eyebrow")}</span>
          <h1>{t("reminder.title")}</h1>
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label={t("guide.backProfile")}
          onClick={onBack}
        >
          <ChevronLeft />
        </button>
      </div>
      <form className="panel form-stack reminder-settings" onSubmit={save}>
        <div className="reminder-settings-heading">
          <span className="list-icon">
            <Bell />
          </span>
          <div>
            <strong>{t("reminder.alertTitle")}</strong>
            <p>{t("reminder.alertDescription")}</p>
          </div>
        </div>
        <label className="switch-row">
          <span>
            <strong>{t("reminder.receive")}</strong>
            <small>{t("reminder.phone", { phone: user.phone || t("common.unregistered") })}</small>
          </span>
          <input
            type="checkbox"
            role="switch"
            aria-label={t("reminder.receive")}
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
        </label>
        <p className="subtle-note">{t("reminder.note")}</p>
        {error && <p className="form-error">{error}</p>}
        {message && <p className="form-message">{message}</p>}
        <button className="primary-button" disabled={busy}>
          {busy ? t("common.saving") : t("reminder.save")}
        </button>
      </form>
      <form className="panel form-stack work-days-settings" onSubmit={saveWorkDays}>
        <div className="reminder-settings-heading">
          <span className="list-icon">
            <CalendarDays />
          </span>
          <div>
            <strong>{t("reminder.workdays")}</strong>
            <p>{t("reminder.workdaysDescription")}</p>
          </div>
        </div>
        <fieldset className="weekday-picker">
          <legend>{t("reminder.chooseWorkdays")}</legend>
          <div className="weekday-options">
            {WEEKDAY_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={workDays.includes(option.value) ? "is-selected" : ""}
              >
                <input
                  type="checkbox"
                  aria-label={t(`weekday.${option.value}.long` as TranslationKey)}
                  checked={workDays.includes(option.value)}
                  onChange={() => toggleWorkDay(option.value)}
                />
                <span>{t(`weekday.${option.value}.short` as TranslationKey)}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <p className="subtle-note">{t("reminder.defaultWorkdays")}</p>
        {workDaysMessage && <p className="form-message">{workDaysMessage}</p>}
        <button className="outline-button" disabled={busy}>
          {busy ? t("common.saving") : t("reminder.saveWorkdays")}
        </button>
      </form>
      <section className="panel form-stack reminder-settings">
        <div className="reminder-settings-heading">
          <span className="list-icon"><Bell /></span>
          <div><strong>{t("reminder.pushTitle")}</strong><p>{t("reminder.pushDescription")}</p></div>
        </div>
        {pushState === "needs-install" && <p className="subtle-note">{t("reminder.pushInstallHint")}</p>}
        {pushError && <p className="form-error">{pushError}</p>}
        <button
          type="button"
          className={pushState === "enabled" ? "outline-button" : "primary-button"}
          disabled={busy || pushState === "unsupported" || pushState === "denied" || pushState === "needs-install"}
          onClick={() => void togglePush()}
        >
          {pushState === "enabled" ? t("reminder.pushDisable") :
            pushState === "denied" ? t("reminder.pushDenied") :
              pushState === "needs-install" ? t("reminder.pushNeedsInstall") :
              pushState === "unsupported" ? t("reminder.pushUnsupported") : t("reminder.pushEnable")}
        </button>
      </section>
    </main>
  );
}

function GuidePage({ onBack, onboarding = false }: { onBack: () => void; onboarding?: boolean }) {
  const { t } = useI18n();
  const steps = [
    { title: t("guide.meals.title"), body: t("guide.meals.description") },
    { title: t("guide.bulk.title"), body: t("guide.bulk.description") },
    { title: t("guide.vacation.title"), body: t("guide.vacation.description") },
    { title: t("guide.reminder.title"), body: t("guide.reminder.description") },
    { title: t("guide.notices.title"), body: t("guide.notices.description") },
    { title: t("guide.history.title"), body: t("guide.history.description") },
    { title: t("guide.settings.title"), body: t("guide.settings.description") },
    { title: t("guide.consumption.title"), body: t("guide.consumption.description") },
  ];
  return (
    <main className="page guide-page">
      <div className="page-heading">
        <div>
          <span>{t("guide.eyebrow")}</span>
          <h1>{t("guide.title")}</h1>
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label={onboarding ? t("common.exit") : t("guide.backProfile")}
          onClick={onBack}
        >
          <ChevronLeft />
        </button>
      </div>
      <div className="guide-deadline">
        <Clock3 size={20} />
        <div>
          <strong>{t("guide.deadline.title")}</strong>
          <p>{t("guide.deadline.description")}</p>
        </div>
      </div>
      <ol className="guide-list">
        {steps.map((step, index) => (
          <li key={step.title}>
            <span>{index + 1}</span>
            <div>
              <h2>{step.title}</h2>
              <p>{step.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </main>
  );
}

function MealHistoryPage({
  orders,
  onBack,
  onConsumptionSaved,
}: {
  orders: Order[];
  onBack: () => void;
  onConsumptionSaved?: () => Promise<void>;
}) {
  const { t, language, formatDate } = useI18n();
  const [allOrders, setAllOrders] = useState(orders);
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);
  useEffect(() => {
    void api<Order[]>("/api/orders/history")
      .then(setAllOrders)
      .catch(() => undefined);
  }, []);
  const history = [...allOrders].sort((a, b) => b.date.localeCompare(a.date));
  return (
    <main className="page">
      <div className="page-heading">
        <div>
          <span>{t("profile.eyebrow")}</span>
          <h1>{t("history.title")}</h1>
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label={t("guide.backProfile")}
          onClick={onBack}
        >
          <ChevronLeft />
        </button>
      </div>
      {history.length ? (
        <div className="card-list">
          {history.map((order) => {
            const canConfirm = isActualMealOrder(order) && canReportMealConsumption(order.date);
            const open = openOrderId === order.id;
            const badge = !canConfirm ? null : order.consumptionStatus === "eaten" ? "eaten" : order.consumptionStatus === "uneaten" ? "uneaten" : "unanswered";
            return (
              <article className={`history-item${open ? " is-open" : ""}`} key={order.id}>
                <button
                  type="button"
                  className="list-card history-item-toggle"
                  onClick={() => setOpenOrderId(open ? null : order.id)}
                  disabled={!canConfirm}
                >
                  <span className="list-icon">{order.emoji}</span>
                  <div>
                    <strong>{order.menuName}</strong>
                    <p>{t("history.location", { location: order.locationName })}</p>
                    <time dateTime={order.date}>{language === "ko" ? historyDate(order.date) : formatDate(`${order.date}T00:00:00Z`, { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" })}</time>
                  </div>
                  {badge ? <span className={`history-consumption-badge is-${badge}`}>{t(`history.consumption.${badge}`)}</span> : null}
                  {canConfirm ? <ChevronDown className={open ? "is-open" : ""} /> : null}
                </button>
                {open && canConfirm ? (
                  <MealConsumptionCard
                    order={order}
                    date={order.date}
                    compact
                    onSaved={(status) => {
                      setAllOrders((current) => current.map((item) => item.id === order.id ? { ...item, consumptionStatus: status } : item));
                      void onConsumptionSaved?.();
                    }}
                  />
                ) : null}
              </article>
            );
          })}
        </div>
      ) : (
        <Empty text={t("history.empty")} />
      )}
    </main>
  );
}

function PasswordSettingsPage({ onBack }: { onBack: () => void }) {
  const { t, errorMessage } = useI18n();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setMessage("");
    setError("");
    if (newPassword !== confirmPassword)
      return setError(t("error.PASSWORD_MISMATCH"));
    setBusy(true);
    try {
      await api(
        "/api/auth/password",
        json("POST", { currentPassword, newPassword }),
      );
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setMessage(t("password.changed"));
    } catch (problem) {
      setError(errorMessage(problem));
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="page">
      <div className="page-heading">
        <div>
          <span>{t("profile.eyebrow")}</span>
          <h1>{t("profile.password.title")}</h1>
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label={t("guide.backProfile")}
          onClick={onBack}
        >
          <ChevronLeft />
        </button>
      </div>
      <form className="panel form-stack" onSubmit={submit}>
        <label>
          {t("password.current")}
          <input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            required
          />
        </label>
        <label>
          {t("onboarding.password.new")}
          <input
            type="password"
            autoComplete="new-password"
            minLength={4}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            required
          />
        </label>
        <label>
          {t("onboarding.password.confirm")}
          <input
            type="password"
            autoComplete="new-password"
            minLength={4}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            required
          />
        </label>
        {error && <p className="form-error">{error}</p>}
        {message && <p className="form-message">{message}</p>}
        <button className="primary-button" disabled={busy}>
          <KeyRound size={18} />
          {busy ? t("password.changing") : t("password.action")}
        </button>
      </form>
    </main>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div className="empty">
      <span>🍽️</span>
      <p>{text}</p>
    </div>
  );
}

function AdminPage({
  appData,
  onDataChange,
  section,
  onSection,
}: {
  appData: AppData;
  onDataChange: () => Promise<void>;
  section: AdminSection;
  onSection: (section: AdminSection) => void;
}) {
  const { t } = useI18n();
  const items: Array<[AdminSection, string, typeof Home]> = [
    ["dashboard", t("admin.dashboard"), Home],
    ["orders", t("admin.orders"), Clipboard],
    ["consumption", t("admin.consumption"), Check],
    ["users", t("admin.users"), Users],
    ["rooms", t("admin.rooms"), Clock3],
    ["menus", t("admin.menus"), Utensils],
    ["notices", t("admin.notices"), Megaphone],
    ["feedback", t("admin.feedback"), MessageSquare],
    ["auto-push", t("admin.autoPush"), Clock3],
    ["push", t("admin.manualPush"), Bell],
    ["settlement", t("admin.settlement"), WalletCards],
  ];
  return (
    <main className="admin-layout">
      <aside className="admin-sidebar">
        <nav>
          {items.map(([value, label, Icon]) => (
            <button
              key={value}
              className={section === value ? "is-active" : ""}
              onClick={() => onSection(value)}
            >
              <Icon size={19} />
              {label}
            </button>
          ))}
        </nav>
      </aside>
      <section className="admin-content">
        {section === "dashboard" && <AdminDashboard onMove={onSection} />}
        {section === "orders" && (
          <AdminOrders settlement={false} appData={appData} />
        )}
        {section === "consumption" && <AdminConsumption locations={appData.locations} />}
        {section === "settlement" && (
          <AdminOrders settlement appData={appData} />
        )}
        {section === "users" && <AdminUsers data={appData} />}
        {section === "rooms" && <AdminMeetingRooms />}
        {section === "menus" && <AdminMenus />}
        {section === "notices" && (
          <AdminNotices onChange={onDataChange} />
        )}
        {section === "feedback" && <AdminFeedback />}
        {section === "auto-push" && <AdminAutoPush />}
        {section === "push" && <AdminManualPush appData={appData} />}
      </section>
    </main>
  );
}

type FeedbackImage = { id: string; url: string; fileName: string };
type AdminFeedbackItem = {
  id: string;
  authorName: string;
  organizationName: string | null;
  locationName: string | null;
  createdAt: string;
  content: string;
  images: FeedbackImage[];
};

function AdminFeedback() {
  const { t, errorMessage } = useI18n();
  const [items, setItems] = useState<AdminFeedbackItem[] | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [completing, setCompleting] = useState(false);
  const [preview, setPreview] = useState<{ image: FeedbackImage; index: number } | null>(null);
  const [error, setError] = useState("");
  const pendingFeedbacks = items ?? [];
  const safePageIndex = Math.min(pageIndex, Math.max(0, pendingFeedbacks.length - 1));
  const feedback = pendingFeedbacks[safePageIndex];

  const loadFeedbacks = useCallback(async () => {
    try {
      const data = await api<AdminFeedbackItem[]>("/api/admin/feedbacks");
      setItems(Array.isArray(data) ? data : []);
      setError("");
    } catch (problem) {
      setItems([]);
      setError(errorMessage(problem));
    }
  }, [errorMessage]);

  useEffect(() => {
    void loadFeedbacks();
  }, [loadFeedbacks]);

  async function completeFeedback(id: string) {
    if (completing) return;
    setCompleting(true);
    setError("");
    try {
      await api(`/api/admin/feedbacks/${id}/complete`, json("POST"));
      setItems((current) => (current ?? []).filter((item) => item.id !== id));
      setPreview(null);
    } catch (problem) {
      setError(errorMessage(problem));
    } finally {
      setCompleting(false);
    }
  }

  return (
    <>
      <AdminTitle eyebrow={t("admin.feedback.eyebrow")} title={t("admin.feedback.title")} />
      {error ? <p className="form-message is-error" role="alert">{error}</p> : null}
      {items === null ? (
        <p className="admin-feedback-empty">{t("admin.feedback.loading")}</p>
      ) : feedback ? <>
        <div className="admin-feedback-grid">
          <article className="admin-feedback-card">
            <header>
              <strong>{feedback.authorName}</strong>
              <time dateTime={feedback.createdAt.replace(" ", "T")}>
                <small>{t("admin.feedback.createdAt")}</small>
                {feedback.createdAt}
              </time>
            </header>
            <dl>
              <div>
                <dt>{t("admin.feedback.organization")}</dt>
                <dd>{feedback.organizationName || t("common.unassigned")}</dd>
              </div>
              <div>
                <dt>{t("admin.feedback.location")}</dt>
                <dd>{feedback.locationName || t("common.unassigned")}</dd>
              </div>
            </dl>
            <p>{feedback.content}</p>
            {feedback.images.length ? (
              <div className="admin-feedback-images">
                <strong>{t("admin.feedback.photos")}</strong>
                <div className="feedback-photo-grid">
                  {feedback.images.map((image, index) => (
                    <button
                      type="button"
                      key={image.id}
                      aria-label={`${t("admin.feedback.photo")} ${index + 1} ${t("admin.feedback.enlarge")}`}
                      onClick={() => setPreview({ image, index })}
                    >
                      <img src={image.url} alt={`${t("admin.feedback.photo")} ${index + 1}`} />
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            <button
              type="button"
              className="primary-button feedback-complete-button"
              disabled={completing}
              onClick={() => void completeFeedback(feedback.id)}
            >
              {completing ? t("admin.feedback.completing") : t("admin.feedback.complete")}
            </button>
          </article>
        </div>
        <nav className="admin-feedback-pagination" aria-label={t("admin.feedback.pagination")}>
          <button type="button" className="outline-button" disabled={safePageIndex === 0} onClick={() => setPageIndex(safePageIndex - 1)}>
            {t("admin.feedback.previous")}
          </button>
          <span>{t("admin.feedback.page", { current: safePageIndex + 1, total: pendingFeedbacks.length })}</span>
          <button type="button" className="outline-button" disabled={safePageIndex === pendingFeedbacks.length - 1} onClick={() => setPageIndex(safePageIndex + 1)}>
            {t("admin.feedback.next")}
          </button>
        </nav>
      </> : <p className="admin-feedback-empty">{t("admin.feedback.empty")}</p>}
      {preview && feedback ? (
        <div className="image-lightbox" role="dialog" aria-modal="true" aria-label={`${t("admin.feedback.photo")} ${t("admin.feedback.enlarge")}`}>
          <button type="button" className="icon-button" aria-label={t("admin.feedback.closeImage")} onClick={() => setPreview(null)}>
            <X />
          </button>
          <img src={preview.image.url} alt={`${t("admin.feedback.photo")} ${preview.index + 1} ${t("admin.feedback.enlarge")}`} />
        </div>
      ) : null}
    </>
  );
}

type PushReminderSettings = {
  id: string;
  name: string;
  enabled: boolean;
  sendTime: string;
  title: string;
  bodyTemplate: string;
};

type AdminPushSummary = {
  selectedUsers: number;
  subscribedUsers: number;
  queuedDevices: number;
  sentDevices: number;
  failedDevices: number;
  expiredDevices: number;
};

type AdminPushHealth = {
  configured: boolean;
  activeDevices: number;
  subscribedUsers: number;
  weekday: number;
  workDaysDistribution: Array<{ workDays: string; devices: number }>;
  recentDeliveries: Array<{ kind: string; status: string; error: string | null; count: number }>;
  automaticRules: Array<{ id: string; name: string; sendTime: string; subscribedAtRun: number; consentedAtRun: number; workdayAtRun: number; notOrderedAtRun: number; eligibleDevicesAtRun: number; claimedDevices: number }>;
};

const EMPTY_AUTO_PUSH: Omit<PushReminderSettings, "id"> = {
  name: "",
  enabled: true,
  sendTime: "09:30",
  title: "식단을 신청해주세요",
  bodyTemplate: "{이름}님의 오늘 식단을 알려주세요!",
};

function PushTemplateField({
  id,
  label,
  maxLength,
  multiline = false,
  value,
  onChange,
}: {
  id: string;
  label: string;
  maxLength: number;
  multiline?: boolean;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useI18n();
  const fieldRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  function insertNameProperty() {
    const field = fieldRef.current;
    const token = "{이름}";
    const selectionStart = field?.selectionStart ?? value.length;
    const selectionEnd = field?.selectionEnd ?? value.length;
    const nextValue = `${value.slice(0, selectionStart)}${token}${value.slice(selectionEnd)}`;
    if (nextValue.length > maxLength) return;
    onChange(nextValue);
    requestAnimationFrame(() => {
      const cursor = selectionStart + token.length;
      field?.focus();
      field?.setSelectionRange(cursor, cursor);
    });
  }

  return (
    <div className="push-message-field">
      <div className="push-message-heading">
        <label htmlFor={id}>{label}</label>
        <button type="button" className="property-button" aria-label={t("admin.push.addNameProperty")} onClick={insertNameProperty}>
          + {t("admin.push.nameProperty")}
        </button>
      </div>
      {multiline
        ? <textarea ref={(node) => { fieldRef.current = node; }} id={id} maxLength={maxLength} value={value} onChange={(event) => onChange(event.target.value)} required />
        : <input ref={(node) => { fieldRef.current = node; }} id={id} maxLength={maxLength} value={value} onChange={(event) => onChange(event.target.value)} required />}
      <small className="character-count">{value.length}/{maxLength}</small>
    </div>
  );
}

function AdminAutoPush() {
  const { t, errorMessage } = useI18n();
  const [rules, setRules] = useState<PushReminderSettings[] | null>(null);
  const [draft, setDraft] = useState<Omit<PushReminderSettings, "id"> | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function loadRules() {
    setRules(await api<PushReminderSettings[]>("/api/admin/push/reminder-settings"));
  }

  useEffect(() => {
    loadRules().catch((problem) => setError(errorMessage(problem)));
  }, [errorMessage]);

  function startCreate() {
    setEditingId(null);
    setDraft({ ...EMPTY_AUTO_PUSH });
    setError("");
    setMessage("");
  }

  function startEdit(rule: PushReminderSettings) {
    setEditingId(rule.id);
    setDraft({
      name: rule.name,
      enabled: rule.enabled,
      sendTime: rule.sendTime,
      title: rule.title,
      bodyTemplate: rule.bodyTemplate,
    });
    setError("");
    setMessage("");
  }

  async function saveRule(event: FormEvent) {
    event.preventDefault();
    if (!draft) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      if (editingId) {
        await api<PushReminderSettings>(`/api/admin/push/reminder-settings/${editingId}`, json("PUT", draft));
        setMessage(t("admin.push.settingsSaved"));
      } else {
        await api<PushReminderSettings>("/api/admin/push/reminder-settings", json("POST", draft));
        setMessage(t("admin.push.created"));
      }
      setDraft(null);
      setEditingId(null);
      await loadRules();
    } catch (problem) {
      setError(errorMessage(problem));
    } finally {
      setBusy(false);
    }
  }

  async function toggleRule(rule: PushReminderSettings, enabled: boolean) {
    setError("");
    setMessage("");
    try {
      await api<PushReminderSettings>(`/api/admin/push/reminder-settings/${rule.id}`, json("PUT", {
        name: rule.name,
        enabled,
        sendTime: rule.sendTime,
        title: rule.title,
        bodyTemplate: rule.bodyTemplate,
      }));
      setRules((current) => current?.map((item) => item.id === rule.id ? { ...item, enabled } : item) ?? null);
    } catch (problem) {
      setError(errorMessage(problem));
    }
  }

  async function deleteRule(rule: PushReminderSettings) {
    if (!window.confirm(t("admin.push.deleteConfirm"))) return;
    setError("");
    setMessage("");
    try {
      await api(`/api/admin/push/reminder-settings/${rule.id}`, { method: "DELETE" });
      if (editingId === rule.id) {
        setDraft(null);
        setEditingId(null);
      }
      setMessage(t("admin.push.deleted"));
      await loadRules();
    } catch (problem) {
      setError(errorMessage(problem));
    }
  }

  return (
    <>
      <AdminTitle eyebrow={t("admin.push.autoEyebrow")} title={t("admin.push.autoTitle")}>
        <button className="primary-button compact" type="button" onClick={startCreate}>
          <Plus /> {t("admin.push.add")}
        </button>
      </AdminTitle>
      {error && <p className="form-error">{error}</p>}
      {message && <p className="form-message">{message}</p>}
      {draft && (
        <form className="admin-panel form-stack" onSubmit={saveRule}>
          <div className="section-title">
            <h2>{editingId ? t("admin.push.edit") : t("admin.push.newRule")}</h2>
          </div>
          <label className="switch-row">
            <span><strong>{t("admin.push.enabled")}</strong><small>{t("admin.push.enabledDescription")}</small></span>
            <input type="checkbox" role="switch" checked={draft.enabled} onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })} />
          </label>
          <label>{t("admin.push.ruleName")}<input value={draft.name} maxLength={40} onChange={(event) => setDraft({ ...draft, name: event.target.value })} required /></label>
          <label>{t("admin.push.time")}<input type="time" value={draft.sendTime} onChange={(event) => setDraft({ ...draft, sendTime: event.target.value })} required /></label>
          <PushTemplateField id="automatic-push-title" label={t("admin.push.messageTitle")} maxLength={40} value={draft.title} onChange={(title) => setDraft({ ...draft, title })} />
          <PushTemplateField id="automatic-push-body" label={t("admin.push.messageBody")} maxLength={120} multiline value={draft.bodyTemplate} onChange={(bodyTemplate) => setDraft({ ...draft, bodyTemplate })} />
          <div className="push-preview"><strong>{draft.title.replaceAll("{이름}", "김닥터")}</strong><p>{draft.bodyTemplate.replaceAll("{이름}", "김닥터")}</p></div>
          <div className="push-rule-form-actions">
            <button className="outline-button" type="button" onClick={() => { setDraft(null); setEditingId(null); }}>{t("admin.push.cancelEdit")}</button>
            <button className="primary-button" disabled={busy}>{t("admin.push.saveSettings")}</button>
          </div>
        </form>
      )}
      <section className="admin-panel">
        <div className="section-title"><h2>{t("admin.push.automatic")}</h2></div>
        {rules === null ? <p>{t("common.loading")}</p> : rules.length === 0 ? (
          <p className="subtle-note">{t("admin.push.empty")}</p>
        ) : (
          <div className="push-rule-list">
            {rules.map((rule) => (
              <article key={rule.id} className={`push-rule-card${rule.enabled ? "" : " is-disabled"}`}>
                <label className="switch-row">
                  <span>
                    <strong>{rule.name}</strong>
                    <small>{rule.sendTime} · {rule.title}</small>
                  </span>
                  <input type="checkbox" role="switch" checked={rule.enabled} onChange={(event) => void toggleRule(rule, event.target.checked)} aria-label={`${rule.name} ${t("admin.push.enabled")}`} />
                </label>
                <div className="push-rule-actions">
                  <button className="outline-button" type="button" onClick={() => startEdit(rule)}>
                    <Pencil size={16} /> {t("admin.push.edit")}
                  </button>
                  <button className="danger-button" type="button" onClick={() => void deleteRule(rule)}>
                    <Trash2 size={16} /> {t("admin.push.delete")}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function AdminManualPush({ appData }: { appData: AppData }) {
  const { t, errorMessage } = useI18n();
  const [users, setUsers] = useState<User[]>([]);
  const [health, setHealth] = useState<AdminPushHealth | null>(null);
  const [healthError, setHealthError] = useState("");
  const [targetType, setTargetType] = useState<"all" | "organizations" | "users">("all");
  const [targetIds, setTargetIds] = useState<string[]>([]);
  const [userQuery, setUserQuery] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [summary, setSummary] = useState<AdminPushSummary | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<User[]>("/api/admin/users")
      .then((employees) => setUsers(employees.filter((employee) => employee.active !== false)))
      .catch((problem) => setError(errorMessage(problem)));
  }, [errorMessage]);

  const loadHealth = useCallback(async () => {
    try {
      setHealth(await api<AdminPushHealth>("/api/admin/push/health"));
      setHealthError("");
    } catch (problem) {
      setHealthError(errorMessage(problem));
    }
  }, [errorMessage]);
  useEffect(() => { void loadHealth(); }, [loadHealth]);

  const filteredUsers = useMemo(() => {
    const needle = userQuery.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((employee) => [employee.name, employee.email, employee.organizationName]
      .some((value) => value?.toLowerCase().includes(needle)));
  }, [users, userQuery]);

  function toggleTarget(id: string) {
    setTargetIds((current) => current.includes(id)
      ? current.filter((value) => value !== id)
      : [...current, id]);
  }

  async function send(event: FormEvent) {
    event.preventDefault();
    if (targetType !== "all" && !targetIds.length) {
      setError(t("admin.push.targetRequired"));
      return;
    }
    if (!window.confirm(t("admin.push.confirm"))) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await api<AdminPushSummary>("/api/admin/push/send", json("POST", {
        target: { type: targetType, ids: targetType === "all" ? [] : targetIds },
        title,
        body,
      }));
      setSummary(result);
      setMessage(result.subscribedUsers === 0
        ? t("admin.push.noDevices")
        : result.queuedDevices === 0
          ? t("admin.push.failed")
          : t("admin.push.queued", { count: result.queuedDevices }));
    } catch (problem) {
      setError(errorMessage(problem));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <AdminTitle eyebrow={t("admin.push.manualEyebrow")} title={t("admin.push.manualTitle")} />
      <section className="admin-panel">
        <div className="section-title">
          <h2>앱푸시 상태</h2>
          <button type="button" className="outline-button" onClick={() => void loadHealth()}>상태 새로고침</button>
        </div>
        {health && <>
          <div className="push-result">
            <span>{health.configured ? "발송 설정 완료" : "발송 설정 미완료"}</span>
            <span>등록 기기 {health.activeDevices}대 · 사용자 {health.subscribedUsers}명</span>
          </div>
          <ul className="push-health-list">
            {(health.automaticRules ?? []).map((rule) => <li key={rule.id}>
              <strong>{rule.name}</strong>
              <span>{rule.sendTime} 대상 추정 {rule.eligibleDevicesAtRun}대 · 발송 기록 {rule.claimedDevices}건</span>
              <span>당시 구독 {rule.subscribedAtRun}대 · 동의 {rule.consentedAtRun}대 · 근무 {rule.workdayAtRun}대 · 미신청 {rule.notOrderedAtRun}대</span>
            </li>)}
          </ul>
          <details className="push-health-details">
            <summary>대상 조건 진단 · 오늘 요일 코드 {health.weekday}</summary>
            <ul className="push-health-list">
              {(health.workDaysDistribution ?? []).map((item) => <li key={item.workDays}>
                <span>근무 요일 저장값 {item.workDays || "미지정"} · {item.devices}대</span>
              </li>)}
            </ul>
          </details>
          <ul className="push-health-list">
            {(health.recentDeliveries ?? []).map((delivery) => <li key={`${delivery.kind}-${delivery.status}-${delivery.error}`}>
              <strong>{delivery.kind === "admin" ? "수동" : "자동"} · {delivery.status}</strong>
              <span>{delivery.error || "오류 없음"} · {delivery.count}건</span>
            </li>)}
          </ul>
        </>}
        {healthError && <p className="form-error">{healthError}</p>}
      </section>
      <form className="admin-panel form-stack" onSubmit={send}>
        <div className="section-title"><h2>{t("admin.push.compose")}</h2></div>
        <fieldset className="push-target-options">
          <legend>{t("admin.push.target")}</legend>
          {(["all", "organizations", "users"] as const).map((type) => (
            <label key={type} className={targetType === type ? "is-selected" : ""}><input type="radio" name="push-target" checked={targetType === type} onChange={() => { setTargetType(type); setTargetIds([]); setUserQuery(""); }} />{t(`admin.push.target.${type}` as TranslationKey)}</label>
          ))}
        </fieldset>
        {targetType === "organizations" && <div className="push-select-list">
          {appData.organizations.map((organization) => <label key={organization.id} className={targetIds.includes(organization.id) ? "is-selected" : ""}><input type="checkbox" checked={targetIds.includes(organization.id)} onChange={() => toggleTarget(organization.id)} />{organization.name}</label>)}
        </div>}
        {targetType === "users" && (
          <div className="push-user-picker">
            <label className="push-user-search">
              {t("admin.push.searchUsers")}
              <input type="search" value={userQuery} placeholder={t("admin.push.searchPlaceholder")} onChange={(event) => setUserQuery(event.target.value)} />
            </label>
            <p className="subtle-note">{t("admin.push.selectedCount", { count: targetIds.length })}</p>
            {filteredUsers.length ? (
              <div className="push-select-list">
                {filteredUsers.map((employee) => <label key={employee.id} className={targetIds.includes(employee.id) ? "is-selected" : ""}><input type="checkbox" checked={targetIds.includes(employee.id)} onChange={() => toggleTarget(employee.id)} />{employee.name} · {employee.organizationName || t("common.unassigned")}</label>)}
              </div>
            ) : <p className="subtle-note">{t("admin.push.searchEmpty")}</p>}
          </div>
        )}
        <PushTemplateField id="manual-push-title" label={t("admin.push.messageTitle")} maxLength={40} value={title} onChange={setTitle} />
        <PushTemplateField id="manual-push-body" label={t("admin.push.messageBody")} maxLength={120} multiline value={body} onChange={setBody} />
        <button className="primary-button" disabled={busy}><Bell size={18} />{t("admin.push.send")}</button>
        {summary && <div className="push-result">
          <span>{t("admin.push.resultUsers", { count: summary.selectedUsers })}</span>
          <span>{t("admin.push.resultSubscribed", { count: summary.subscribedUsers })}</span>
          <span>{t("admin.push.resultQueued", { count: summary.queuedDevices })}</span>
        </div>}
      </form>
      {error && <p className="form-error">{error}</p>}
      {message && <p className="form-message">{message}</p>}
    </>
  );
}

function AdminTitle({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="admin-title">
      <div>
        <span>{eyebrow}</span>
        <h1>{title}</h1>
      </div>
      {children}
    </div>
  );
}

function AdminDashboard({ onMove }: { onMove: (value: AdminSection) => void }) {
  const { t } = useI18n();
  const [overview, setOverview] = useState<{
    total: number;
    ordered: number;
    vacation: number;
    pending: number;
    notices: number;
  } | null>(null);
  useEffect(() => {
    api<typeof overview>("/api/admin/overview").then(setOverview);
  }, []);
  const cards = overview
    ? ([
        [t("admin.totalUsers"), overview.total, Users],
        [t("admin.ordered"), overview.ordered, Check],
        [t("admin.vacation"), overview.vacation, Palmtree],
        [t("admin.pending"), overview.pending, Bell],
      ] as const)
    : [];
  return (
    <>
      <AdminTitle eyebrow={t("admin.today.eyebrow")} title={t("admin.today.title")}>
        <button
          className="primary-button compact"
          onClick={() => onMove("orders")}
        >
          {t("admin.viewOrders")}
        </button>
      </AdminTitle>
      <div className="stat-grid">
        {cards.map(([label, count, Icon]) => (
          <article
            key={label}
            className={label === t("admin.pending") && count > 0 ? "is-danger" : ""}
          >
            <Icon />
            <span>{label}</span>
            <strong>
              {count}
              <small />
            </strong>
          </article>
        ))}
      </div>
      <section className="admin-panel">
        <div className="section-title">
          <h2>{t("admin.todo")}</h2>
        </div>
        <button className="action-row" onClick={() => onMove("orders")}>
          <span className="list-icon">
            <Clipboard />
          </span>
          <div>
            <strong>{t("admin.restaurantOrders")}</strong>
            <p>{t("admin.restaurantOrdersDescription")}</p>
          </div>
          <ChevronRight />
        </button>
        <button className="action-row" onClick={() => onMove("users")}>
          <span className="list-icon">
            <Users />
          </span>
          <div>
            <strong>{t("admin.manageUsers")}</strong>
            <p>{t("admin.manageUsersDescription")}</p>
          </div>
          <ChevronRight />
        </button>
      </section>
    </>
  );
}

type AdminOrderItem = {
  id: string;
  date: string;
  userId?: string;
  menuId?: string;
  locationId?: string;
  employee: string;
  organization: string;
  location: string;
  menu: string;
  price: number;
  status: string;
  consumptionStatus?: MealConsumptionStatus | null;
};
type AdminOrderData = {
  orders: AdminOrderItem[];
  aggregation: {
    totalCount: number;
    totalAmount: number;
    locations: Array<{
      location: string;
      count: number;
      amount: number;
      menus: Array<{ menu: string; count: number; amount: number }>;
    }>;
  };
  consumption?: {
    eligible: number;
    eaten: number;
    uneaten: number;
    unanswered: number;
  };
  vendorMessage: string | null;
  vendorDelivery?: {
    status: "pending" | "accepted" | "failed";
    toPhone: string;
    error: string | null;
    updatedAt: string;
  } | null;
  vendorPhone?: string | null;
  vendorDeliveries?: VendorSmsLog[];
};

type MealReminderDeliverySummary = {
  date: string;
  eligibleAtRun: number;
  deliveries: Array<{
    status: "pending" | "accepted" | "failed";
    count: number;
    error: string | null;
    firstUpdatedAt: string;
    lastUpdatedAt: string;
  }>;
};

type VendorSmsLog = {
  id: string;
  mealDate: string;
  status: "pending" | "accepted" | "failed";
  toPhone: string;
  fromPhone: string;
  body: string;
  error: string | null;
  providerMessageId: string | null;
  providerGroupId: string | null;
  createdAt: string;
  updatedAt: string;
};

function vendorSendNote(
  date: string,
  delivery?: AdminOrderData["vendorDelivery"],
) {
  if (delivery?.status === "accepted") {
    return `${formatKoreanMobile(delivery.toPhone)}로 자동 전송했습니다.`;
  }
  if (delivery?.status === "failed") {
    return `업체 자동 전송에 실패했습니다. ${delivery.error ?? ""}`.trim();
  }
  if (delivery?.status === "pending") {
    return "업체 자동 전송을 진행 중입니다.";
  }
  if (date === kstToday()) {
    return "오전 10:30 마감 직후 업체 휴대폰으로 자동 전송됩니다.";
  }
  return "이 날짜에는 업체 자동 전송 기록이 없습니다.";
}

function vendorStatusLabel(status: VendorSmsLog["status"]) {
  if (status === "accepted") return "전송 완료";
  if (status === "failed") return "전송 실패";
  return "전송 중";
}

function reminderStatusLabel(
  status: MealReminderDeliverySummary["deliveries"][number]["status"],
) {
  if (status === "accepted") return "접수 완료";
  if (status === "failed") return "접수 실패";
  return "접수 중";
}

function formatSmsLogTime(value: string) {
  const utc = /Z$|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value.replace(" ", "T")}Z`;
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(utc));
}

type AdminOrderDraft = {
  date: string;
  userId: string;
  menuId: string;
  locationId: string;
  editing: boolean;
};

function AdminConsumption({ locations }: { locations: Option[] }) {
  const { t } = useI18n();
  const today = kstToday();
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [orders, setOrders] = useState<AdminOrderItem[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<"uneaten" | "eaten" | "unanswered">("unanswered");
  const [page, setPage] = useState(1);
  const pageSize = 8;
  const load = useCallback(
    () =>
      api<AdminOrderData>(`/api/admin/orders?start=${start}&end=${end}&finalized=0`).then((next) => {
        setOrders(next.orders);
        setPage(1);
      }),
    [start, end],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const eligibleOrders = orders.filter((order) =>
    (!selectedLocationId || order.locationId === selectedLocationId) &&
    order.status !== "vacation" && isActualMealOrder(order),
  );
  const lists = {
    uneaten: eligibleOrders.filter((order) => order.consumptionStatus === "uneaten"),
    eaten: eligibleOrders.filter((order) => order.consumptionStatus === "eaten"),
    unanswered: eligibleOrders.filter((order) => !order.consumptionStatus),
  };
  const selectedRows = lists[selectedStatus];
  const totalPages = Math.max(1, Math.ceil(selectedRows.length / pageSize));
  const visiblePage = Math.min(page, totalPages);
  const pageRows = selectedRows.slice((visiblePage - 1) * pageSize, visiblePage * pageSize);
  const selectedLabel = t(`admin.orders.${selectedStatus}`);
  function selectStatus(status: "uneaten" | "eaten" | "unanswered") {
    setSelectedStatus(status);
    setPage(1);
  }
  return (
    <>
      <AdminTitle eyebrow={t("admin.consumption.eyebrow")} title={t("admin.consumption.title")} />
      <div className="date-filter consumption-filter">
        <label>
          시작일
          <input type="date" value={start} onChange={(event) => setStart(event.target.value)} />
        </label>
        <label>
          종료일
          <input type="date" value={end} onChange={(event) => setEnd(event.target.value)} />
        </label>
        <label>
          식사 장소
          <select value={selectedLocationId} onChange={(event) => { setSelectedLocationId(event.target.value); setPage(1); }}>
            <option value="">전체 장소</option>
            {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
          </select>
        </label>
        <button className="outline-button" onClick={() => void load()}>조회</button>
      </div>
      <div className="stat-grid stat-grid--three">
        <button type="button" aria-label={`${t("admin.orders.uneaten")} ${lists.uneaten.length}명`} className={`stat-card-button ${lists.uneaten.length > 0 ? "is-danger" : ""} ${selectedStatus === "uneaten" ? "is-selected" : ""}`} onClick={() => selectStatus("uneaten")}>
          <Utensils />
          <span>{t("admin.orders.uneaten")}</span>
          <strong>{lists.uneaten.length}<small>명</small></strong>
        </button>
        <button type="button" aria-label={`${t("admin.orders.eaten")} ${lists.eaten.length}명`} className={`stat-card-button ${selectedStatus === "eaten" ? "is-selected" : ""}`} onClick={() => selectStatus("eaten")}>
          <Check />
          <span>{t("admin.orders.eaten")}</span>
          <strong>{lists.eaten.length}<small>명</small></strong>
        </button>
        <button type="button" aria-label={`${t("admin.orders.unanswered")} ${lists.unanswered.length}명`} className={`stat-card-button ${selectedStatus === "unanswered" ? "is-selected" : ""}`} onClick={() => selectStatus("unanswered")}>
          <Bell />
          <span>{t("admin.orders.unanswered")}</span>
          <strong>{lists.unanswered.length}<small>명</small></strong>
        </button>
      </div>
      <section className="admin-panel table-panel">
        <div className="section-title">
          <h2>{selectedLabel}</h2>
          <span>{selectedRows.length}명</span>
        </div>
        {pageRows.length ? (
          <>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>이름</th>
                    <th>소속</th>
                    <th>장소</th>
                    <th>메뉴</th>
                    <th>날짜</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((order) => (
                    <tr key={order.id}>
                      <td><strong>{order.employee}</strong></td>
                      <td>{order.organization || "—"}</td>
                      <td>{order.location || "—"}</td>
                      <td>{order.menu}</td>
                      <td>{order.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <nav className="employee-pagination" aria-label={t("admin.consumption.pagination")}>
              <button
                type="button"
                className="outline-button"
                aria-label="이전 페이지"
                disabled={visiblePage === 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                <ChevronLeft size={18} /> 이전
              </button>
              <strong>{visiblePage} / {totalPages}</strong>
              <button
                type="button"
                className="outline-button"
                aria-label="다음 페이지"
                disabled={visiblePage === totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                다음 <ChevronRight size={18} />
              </button>
            </nav>
          </>
        ) : (
          <p className="subtle-note">{t("admin.consumption.empty")}</p>
        )}
      </section>
    </>
  );
}
function AdminOrders({
  settlement,
  appData,
}: {
  settlement: boolean;
  appData: AppData;
}) {
  const { t } = useI18n();
  const today = kstToday();
  const first = `${today.slice(0, 8)}01`;
  const [start, setStart] = useState(settlement ? first : today);
  const [end, setEnd] = useState(settlement ? today : today);
  const [data, setData] = useState<AdminOrderData | null>(null);
  const [reminderSummary, setReminderSummary] = useState<MealReminderDeliverySummary | null>(null);
  const [copied, setCopied] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [draft, setDraft] = useState<AdminOrderDraft | null>(null);
  const [message, setMessage] = useState("");
  const [vendorPhone, setVendorPhone] = useState("");
  const draftMenus = draft
    ? appData.menus.filter((menu) =>
        menuAvailableOnDate(menu.availableDays, draft.date),
      )
    : [];
  const load = useCallback(
    async () => {
      const [next, reminders] = await Promise.all([
        api<AdminOrderData>(
          `/api/admin/orders?start=${start}&end=${end}&finalized=${settlement ? "1" : "0"}`,
        ),
        settlement
          ? Promise.resolve(null)
          : api<MealReminderDeliverySummary>(
              `/api/admin/meal-reminder-deliveries?date=${start}`,
            ).catch(() => null),
      ]);
      setData(next);
      setReminderSummary(
        reminders &&
          typeof reminders.eligibleAtRun === "number" &&
          Array.isArray(reminders.deliveries)
          ? reminders
          : null,
      );
      if (!settlement) setVendorPhone(next.vendorPhone ? formatKoreanMobile(next.vendorPhone) : "");
    },
    [start, end, settlement],
  );
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (!settlement) void api<User[]>("/api/admin/users").then(setUsers);
  }, [settlement]);
  async function copy() {
    if (!data?.vendorMessage) return;
    await navigator.clipboard.writeText(data.vendorMessage);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }
  async function saveVendorPhone(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isKoreanMobile(vendorPhone)) {
      setMessage("휴대폰 번호를 010-0000-0000 형식으로 입력해 주세요.");
      return;
    }
    try {
      const saved = await api<{ phone: string }>("/api/admin/vendor-sms", json("PUT", { phone: vendorPhone }));
      setVendorPhone(saved.phone);
      setMessage("거래처 번호를 저장했어요.");
    } catch (problem) {
      setMessage((problem as Error).message);
    }
  }
  function downloadCsv() {
    if (!data) return;
    const header = "일자,직원,소속,식사장소,메뉴,금액\n";
    const rows = data.orders
      .map((item) =>
        [
          item.date,
          item.employee,
          item.organization,
          item.location,
          item.menu,
          item.price,
        ].join(","),
      )
      .join("\n");
    const total = `합계,총 식수 ${data.aggregation.totalCount}개,,,,${data.aggregation.totalAmount}`;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(
      new Blob([`\ufeff${header}${rows}${rows ? "\n" : ""}${total}`], {
        type: "text/csv",
      }),
    );
    link.download = `닥터스_${start}_${end}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }
  function openOrder(order?: AdminOrderItem) {
    setMessage("");
    setDraft(
      order
        ? {
            date: order.date,
            userId: order.userId ?? "",
            menuId: order.menuId ?? "",
            locationId: order.locationId ?? "",
            editing: true,
          }
        : {
            date: today,
            userId: "",
            menuId:
              appData.menus.find((menu) =>
                menuAvailableOnDate(menu.availableDays, today),
              )?.id ?? "",
            locationId: appData.locations[0]?.id ?? "",
            editing: false,
          },
    );
  }
  async function saveOrder(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const values = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await api("/api/admin/orders", json("POST", values));
      setDraft(null);
      setMessage("주문을 저장하고 취합 내역을 다시 계산했어요.");
      await load();
    } catch (problem) {
      setMessage((problem as Error).message);
    }
  }
  return (
    <>
      <AdminTitle
        eyebrow={settlement ? t("admin.settlement.eyebrow") : t("admin.orders.eyebrow")}
        title={
          settlement
            ? t("admin.settlement.title")
            : t("admin.orders.title")
        }
      >
        {settlement ? (
          <button className="outline-button" onClick={downloadCsv}>
            CSV 내려받기
          </button>
        ) : (
          <button
            className="primary-button compact"
            onClick={() => openOrder()}
          >
            <Plus size={17} /> 주문 직접 추가
          </button>
        )}
      </AdminTitle>
      {message && <div className="success-banner">{message}</div>}
      {draft && (
        <div className="modal-backdrop">
          <form
            role="dialog"
            aria-modal="true"
            aria-labelledby="order-editor-title"
            className="edit-dialog form-stack"
            onSubmit={saveOrder}
          >
            <div className="section-title">
              <h2 id="order-editor-title">
                {draft.editing ? "주문 수정" : "주문 직접 추가"}
              </h2>
              <button
                type="button"
                className="icon-button"
                aria-label="주문 창 닫기"
                onClick={() => setDraft(null)}
              >
                <X />
              </button>
            </div>
            <label>
              날짜
              <input
                name="date"
                type="date"
                value={draft.date}
                onChange={(event) => {
                  const date = event.target.value;
                  const available = appData.menus.filter((menu) =>
                    menuAvailableOnDate(menu.availableDays, date),
                  );
                  setDraft((current) =>
                    current
                      ? {
                          ...current,
                          date,
                          menuId: available.some(
                            (menu) => menu.id === current.menuId,
                          )
                            ? current.menuId
                            : available[0]?.id ?? "",
                        }
                      : current,
                  );
                }}
                required
              />
            </label>
            <label>
              직원
              <select name="userId" defaultValue={draft.userId} required>
                <option value="" disabled>
                  직원 선택
                </option>
                {users
                  .filter((user) => user.active !== false)
                  .map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} · {user.organizationName || "소속 미지정"}
                    </option>
                  ))}
              </select>
            </label>
            <div className="field-row">
              <label>
                메뉴
                <select
                  name="menuId"
                  value={draft.menuId}
                  onChange={(event) =>
                    setDraft((current) =>
                      current
                        ? { ...current, menuId: event.target.value }
                        : current,
                    )
                  }
                  required
                >
                  {!draftMenus.length && (
                    <option value="">해당 요일에 가능한 메뉴 없음</option>
                  )}
                  {draftMenus.map((menu) => (
                    <option key={menu.id} value={menu.id}>
                      {menu.emoji} {menu.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                식사 장소
                <select
                  name="locationId"
                  defaultValue={draft.locationId}
                  required
                >
                  {appData.locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p className="subtle-note">
              관리자는 오전 10시 30분 마감 이후에도 주문을 추가하거나 수정할 수
              있어요.
            </p>
            <div className="dialog-actions">
              <button
                type="button"
                className="outline-button"
                onClick={() => setDraft(null)}
              >
                취소
              </button>
              <button className="primary-button">
                <Save size={17} /> 주문 저장
              </button>
            </div>
          </form>
        </div>
      )}
      <div className={`date-filter ${settlement ? "" : "date-filter--single"}`}>
        {settlement ? (
          <>
            <label>
              시작일
              <input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </label>
            <label>
              종료일
              <input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </label>
          </>
        ) : (
          <label>
            날짜
            <input
              type="date"
              value={start}
              onChange={(e) => {
                setStart(e.target.value);
                setEnd(e.target.value);
              }}
            />
          </label>
        )}
        <button className="outline-button" onClick={load}>
          조회
        </button>
      </div>
      {settlement && (
        <p className="settlement-note">
          <Check size={16} /> 오전 10시 30분 마감이 지난 신청만 식수와 금액에
          포함됩니다.
        </p>
      )}
      {data && (
        <>
          <div className={`stat-grid ${settlement ? "stat-grid--three" : "stat-grid--two"}`}>
            <article>
              <WalletCards />
              <span>총 식수</span>
              <strong>
                {data.aggregation.totalCount}
                <small>개</small>
              </strong>
            </article>
            <article>
              <Clipboard />
              <span>총 금액</span>
              <strong>
                {data.aggregation.totalAmount.toLocaleString()}
                <small>원</small>
              </strong>
            </article>
            {settlement && (
              <article>
                <CalendarDays />
                <span>조회 기간</span>
                <strong>
                  {datesBetween(start, end).length}
                  <small>일</small>
                </strong>
              </article>
            )}
          </div>
          {!settlement && (
            <>
              <section className="admin-panel">
                <div className="section-title">
                  <h2>알림톡 발송 현황</h2>
                  <span>
                    {(reminderSummary?.deliveries ?? []).reduce(
                      (total, item) => total + item.count,
                      0,
                    )}
                    건
                  </span>
                </div>
                <p className="subtle-note">
                  {reminderSummary
                    ? `09:20 기준 대상 ${reminderSummary.eligibleAtRun}명`
                    : "알림톡 현황을 확인할 수 없습니다."}
                </p>
                {(reminderSummary?.deliveries ?? []).length ? (
                  <div className="location-summary">
                    {(reminderSummary?.deliveries ?? []).map((delivery) => (
                      <article key={delivery.status}>
                        <div>
                          <strong>{reminderStatusLabel(delivery.status)}</strong>
                          <span>
                            {delivery.count}건 · {formatSmsLogTime(delivery.lastUpdatedAt)}
                          </span>
                        </div>
                        {delivery.error && (
                          <p className="subtle-note">{delivery.error}</p>
                        )}
                      </article>
                    ))}
                  </div>
                ) : reminderSummary ? (
                  <p className="subtle-note">알림톡 발송 기록이 없습니다.</p>
                ) : null}
              </section>
              <section className="admin-panel">
              <div className="section-title">
                <h2>거래처 휴대폰</h2>
              </div>
              <form className="vendor-phone-form" onSubmit={saveVendorPhone}>
                <label>
                  번호
                  <input
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    value={vendorPhone}
                    onChange={(event) => setVendorPhone(formatKoreanMobile(event.target.value))}
                    placeholder="010-0000-0000"
                    required
                  />
                </label>
                <button className="primary-button compact" type="submit">
                  <Save size={17} /> 번호 저장
                </button>
              </form>
              <p className="subtle-note">오전 10:30 마감 직후 이 번호로 식단 문자를 보냅니다.</p>
            </section>
            <section className="admin-panel message-panel">
              <div className="section-title">
                <h2>거래처 전달 메시지</h2>
                <button className="outline-button" onClick={copy}>
                  {copied ? <Check /> : <Clipboard />}
                  {copied ? "복사했어요" : "메시지 복사"}
                </button>
              </div>
              <p className="subtle-note">{vendorSendNote(start, data.vendorDelivery)}</p>
              <pre>{data.vendorMessage}</pre>
            </section>
            <section className="admin-panel table-panel">
              <div className="section-title">
                <h2>문자 발송 기록</h2>
                <span>{data.vendorDeliveries?.length ?? 0}건</span>
              </div>
              {data.vendorDeliveries?.length ? (
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>날짜</th>
                        <th>상태</th>
                        <th>수신번호</th>
                        <th>시각</th>
                        <th>메시지 ID</th>
                        <th>본문</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.vendorDeliveries.map((delivery) => (
                        <tr key={delivery.id}>
                          <td>{delivery.mealDate}</td>
                          <td>
                            <span className={`status-pill ${delivery.status === "accepted" ? "status-success" : delivery.status === "failed" ? "status-danger" : "status-muted"}`}>
                              {vendorStatusLabel(delivery.status)}
                            </span>
                          </td>
                          <td>{formatKoreanMobile(delivery.toPhone)}</td>
                          <td>{formatSmsLogTime(delivery.updatedAt)}</td>
                          <td>{delivery.providerMessageId || "—"}</td>
                          <td>
                            <details className="vendor-log-body">
                              <summary>{delivery.error ? delivery.error : "보낸 내용"}</summary>
                              <pre>{delivery.body}</pre>
                            </details>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="subtle-note">아직 발송 기록이 없습니다. 오전 10:30 이후 여기에 결과가 표시됩니다.</p>
              )}
            </section>
            </>
          )}
          <section className="admin-panel">
            <div className="section-title">
              <h2>장소별 합계</h2>
              <span>{data.aggregation.totalCount}개</span>
            </div>
            <div className="location-summary">
              {data.aggregation.locations.map((location) => (
                <article key={location.location}>
                  <div>
                    <strong>{location.location}</strong>
                    <span>
                      {location.count}개 · {location.amount.toLocaleString()}원
                    </span>
                  </div>
                  <ul>
                    {location.menus.map((menu) => (
                      <li key={menu.menu}>
                        <span>{menu.menu}</span>
                        <strong>
                          {menu.count}개 · {menu.amount.toLocaleString()}원
                        </strong>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </section>
          <section className="admin-panel table-panel">
            <div className="section-title">
              <h2>상세 내역</h2>
              <span>{data.orders.length}건</span>
            </div>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>일자</th>
                    <th>직원</th>
                    <th>소속</th>
                    <th>장소</th>
                    <th>메뉴</th>
                    <th>금액</th>
                    {!settlement && <th>관리</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.orders.map((order) => (
                    <tr key={order.id}>
                      <td>{order.date}</td>
                      <td>{order.employee}</td>
                      <td>{order.organization}</td>
                      <td>{order.location}</td>
                      <td>
                        {order.status === "vacation" ? "휴가" : order.menu}
                      </td>
                      <td>
                        {order.status === "vacation"
                          ? 0
                          : order.price.toLocaleString()}
                        원
                      </td>
                      {!settlement && (
                        <td>
                          <div className="inline-actions">
                            <button
                              type="button"
                              disabled={
                                !order.userId ||
                                !order.menuId ||
                                !order.locationId
                              }
                              onClick={() => openOrder(order)}
                            >
                              수정
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}

function AdminUsers({ data }: { data: AppData }) {
  const { t } = useI18n();
  const [users, setUsers] = useState<User[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [message, setMessage] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [employmentView, setEmploymentView] = useState<"active" | "inactive">("active");
  const [organizationFilter, setOrganizationFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [sort, setSort] = useState("name-asc");
  const [page, setPage] = useState(1);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [bulkOrganizationId, setBulkOrganizationId] = useState("");
  const [bulkLocationId, setBulkLocationId] = useState("");
  const [bulkRole, setBulkRole] = useState<"" | User["role"]>("");
  const [bulkActionBusy, setBulkActionBusy] = useState(false);
  const pageSize = 10;
  const load = useCallback(
    () => api<User[]>("/api/admin/users").then(setUsers),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const filteredUsers = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("ko-KR");
    return users
      .filter((user) => employmentView === "inactive" ? user.active === false : user.active !== false)
      .filter((user) => !normalizedQuery || user.name.toLocaleLowerCase("ko-KR").includes(normalizedQuery))
      .filter((user) => !organizationFilter || user.organizationId === organizationFilter)
      .filter((user) => !locationFilter || user.defaultLocationId === locationFilter)
      .sort((left, right) => {
        if (sort === "name-desc") return right.name.localeCompare(left.name, "ko");
        if (sort === "organization-asc") {
          return (left.organizationName || "").localeCompare(right.organizationName || "", "ko") || left.name.localeCompare(right.name, "ko");
        }
        if (sort === "location-asc") {
          return (left.defaultLocationName || "").localeCompare(right.defaultLocationName || "", "ko") || left.name.localeCompare(right.name, "ko");
        }
        return left.name.localeCompare(right.name, "ko");
      });
  }, [users, employmentView, query, organizationFilter, locationFilter, sort]);
  const activeUserCount = users.filter((user) => user.active !== false).length;
  const inactiveUserCount = users.length - activeUserCount;
  function showEmploymentView(view: "active" | "inactive") {
    setEmploymentView(view);
    setSelectedUserIds([]);
    setQuery("");
    setOrganizationFilter("");
    setLocationFilter("");
    setPage(1);
  }
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const visiblePage = Math.min(page, totalPages);
  const pageUsers = filteredUsers.slice((visiblePage - 1) * pageSize, visiblePage * pageSize);
  const allPageUsersSelected = Boolean(pageUsers.length) && pageUsers.every((user) => selectedUserIds.includes(user.id));
  const selectedUsers = users.filter((user) => selectedUserIds.includes(user.id));
  const selectedIncludesAdmin = selectedUsers.some((user) => user.role === "admin");
  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      const result = await api<{ invite: { sent: boolean; message?: string } }>(
        "/api/admin/users",
        json("POST", Object.fromEntries(form)),
      );
      setMessage(
        result.invite.sent
          ? "직원을 등록하고 안내 메일까지 보냈어요."
          : `직원은 등록했어요. ${result.invite.message}`,
      );
      setOpen(false);
      formElement.reset();
      await load();
    } catch (problem) {
      setMessage((problem as Error).message);
    }
  }
  async function reset(id: string) {
    await api(`/api/admin/users/${id}/reset-password`, json("POST"));
    setMessage(
      "비밀번호를 1234로 바꿨어요. 다음 로그인 때 새 비밀번호를 설정합니다.",
    );
  }
  async function resendInvite(user: User) {
    try {
      const result = await api<{ invite: { sent: boolean; message?: string } }>(
        `/api/admin/users/${user.id}/send-invite`,
        json("POST"),
      );
      setMessage(
        result.invite.sent
          ? `${user.name}님에게 안내 메일을 다시 보냈어요.`
          : `메일을 보내지 못했어요. ${result.invite.message ?? "잠시 후 다시 시도해 주세요."}`,
      );
      if (result.invite.sent) await load();
    } catch (problem) {
      setMessage((problem as Error).message);
    }
  }
  async function update(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editing) return;
    const form = new FormData(event.currentTarget);
    try {
      await api(
        `/api/admin/users/${editing.id}`,
        json("PUT", { ...Object.fromEntries(form), active: editing.active }),
      );
      setEditing(null);
      setMessage("직원 정보를 바꿨어요.");
      await load();
    } catch (problem) {
      setMessage((problem as Error).message);
    }
  }
  async function changeEmploymentStatus(user: User) {
    try {
      if (user.active === false) {
        await api(`/api/admin/users/${user.id}`, json("PUT", {
          name: user.name,
          phone: user.phone,
          organizationId: user.organizationId,
          defaultLocationId: user.defaultLocationId,
          role: user.role,
          active: true,
        }));
        setMessage(`${user.name}님을 복귀 처리했어요.`);
      } else {
        if (!window.confirm(`${user.name}님을 퇴사 처리할까요? 과거 주문과 결산 내역은 보존됩니다.`)) return;
        await api(`/api/admin/users/${user.id}`, { method: "DELETE" });
        setMessage(`${user.name}님을 퇴사 처리했어요.`);
      }
      await load();
    } catch (problem) {
      setMessage((problem as Error).message);
    }
  }
  async function uploadEmployees(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setBulkBusy(true);
    setMessage("");
    try {
      const rows = await parseEmployeeImportFile(file);
      const result = await api<{
        created: number;
        failed: Array<{ row: number; message: string }>;
        inviteSent: number;
        inviteFailed: number;
      }>("/api/admin/users/bulk", json("POST", { users: rows }));
      const failedMessage = result.failed.length
        ? ` 등록 실패 ${result.failed.length}건: ${result.failed.map((failure) => `${failure.row}행 ${failure.message}`).join(", ")}`
        : "";
      setMessage(
        `직원 ${result.created}명을 등록하고 안내 메일 ${result.inviteSent}건을 보냈어요.${failedMessage}`,
      );
      await load();
    } catch (problem) {
      setMessage((problem as Error).message);
    } finally {
      setBulkBusy(false);
      event.target.value = "";
    }
  }
  function togglePageSelection() {
    const pageIds = pageUsers.map((user) => user.id);
    setSelectedUserIds((current) => allPageUsersSelected
      ? current.filter((id) => !pageIds.includes(id))
      : [...new Set([...current, ...pageIds])]);
  }
  async function updateSelectedUsers() {
    if (!selectedUserIds.length) return;
    if (!bulkOrganizationId && !bulkLocationId && !bulkRole) {
      return setMessage("일괄 변경할 항목을 하나 이상 선택해 주세요.");
    }
    setBulkActionBusy(true);
    setMessage("");
    try {
      await api("/api/admin/users/bulk", json("PUT", {
        ids: selectedUserIds,
        ...(bulkOrganizationId ? { organizationId: bulkOrganizationId } : {}),
        ...(bulkLocationId ? { defaultLocationId: bulkLocationId } : {}),
        ...(bulkRole ? { role: bulkRole } : {}),
      }));
      setMessage(`직원 ${selectedUserIds.length}명의 정보를 바꿨어요.`);
      setSelectedUserIds([]);
      setBulkOrganizationId("");
      setBulkLocationId("");
      setBulkRole("");
      await load();
    } catch (problem) {
      setMessage((problem as Error).message);
    } finally {
      setBulkActionBusy(false);
    }
  }
  async function deactivateSelectedUsers() {
    if (!selectedUserIds.length) return;
    if (selectedIncludesAdmin) return setMessage("관리자 계정은 일괄 퇴사 처리할 수 없어요.");
    if (!window.confirm(`선택한 직원 ${selectedUserIds.length}명을 퇴사 처리할까요? 과거 주문과 결산 내역은 보존됩니다.`)) return;
    setBulkActionBusy(true);
    setMessage("");
    try {
      await api("/api/admin/users/bulk", json("DELETE", { ids: selectedUserIds }));
      setMessage(`직원 ${selectedUserIds.length}명을 퇴사 처리했어요.`);
      setSelectedUserIds([]);
      setPage(1);
      await load();
    } catch (problem) {
      setMessage((problem as Error).message);
    } finally {
      setBulkActionBusy(false);
    }
  }
  return (
    <>
      <AdminTitle eyebrow={t("admin.users.eyebrow")} title={t("admin.users.title")}>
        <button
          className="primary-button compact"
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          <Plus /> 직원 추가
        </button>
      </AdminTitle>
      {message && <div className="success-banner">{message}</div>}
      <section className="admin-panel bulk-user-panel">
        <div className="section-title">
          <div>
            <h2>직원 일괄 등록</h2>
            <p>엑셀 양식에 직원 정보를 채운 뒤 올려 주세요. CSV도 사용할 수 있어요.</p>
          </div>
        </div>
        <div className="bulk-user-actions">
          <a
            className="outline-button"
            href="/templates/닥터스밀_직원_일괄등록_양식.xlsx"
            download
          >
            <Download size={17} /> 엑셀 양식 내려받기
          </a>
          <label
            className={`outline-button file-button ${bulkBusy ? "is-disabled" : ""}`}
          >
            <Upload size={17} />
            {bulkBusy ? "등록 중..." : "엑셀·CSV 파일 선택"}
            <input
              aria-label="엑셀 또는 CSV 파일 선택"
              type="file"
              accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              disabled={bulkBusy}
              onChange={uploadEmployees}
            />
          </label>
        </div>
      </section>
      {open && (
        <form className="admin-panel form-stack" onSubmit={create}>
          <div className="section-title">
            <h2>새 직원 등록</h2>
            <button
              type="button"
              className="icon-button"
              onClick={() => setOpen(false)}
            >
              <X />
            </button>
          </div>
          <div className="field-row">
            <label>
              이름
              <input name="name" required />
            </label>
            <label>
              이메일
              <input name="email" type="email" required />
            </label>
          </div>
          <div className="field-row">
            <label>
              휴대폰 번호
              <PhoneInput />
            </label>
            <label>
              소속
              <select name="organizationId" required defaultValue="">
                <option value="" disabled>
                  선택
                </option>
                {data.organizations.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label>
            기본 식사 장소
            <select name="defaultLocationId" required defaultValue="">
              <option value="" disabled>
                선택
              </option>
              {data.locations.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <button className="primary-button">
            계정 만들고 안내 메일 보내기
          </button>
        </form>
      )}
      {editing && (
        <div className="modal-backdrop">
          <form
            role="dialog"
            aria-modal="true"
            aria-labelledby="employee-edit-title"
            className="edit-dialog form-stack"
            onSubmit={update}
          >
            <div className="section-title">
              <h2 id="employee-edit-title">직원 정보 수정</h2>
              <button
                type="button"
                className="icon-button"
                aria-label="수정 닫기"
                onClick={() => setEditing(null)}
              >
                <X />
              </button>
            </div>
            <label>
              이메일
              <input value={editing.email} disabled />
            </label>
            <div className="field-row">
              <label>
                이름
                <input name="name" defaultValue={editing.name} required />
              </label>
              <label>
                휴대폰 번호
                <PhoneInput
                  key={editing.id}
                  defaultValue={editing.phone || ""}
                />
              </label>
            </div>
            <div className="field-row">
              <label>
                소속
                <select
                  name="organizationId"
                  required
                  defaultValue={editing.organizationId || ""}
                >
                  <option value="" disabled>
                    선택
                  </option>
                  {data.organizations.map((item) => (
                    <option value={item.id} key={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                기본 식사 장소
                <select
                  name="defaultLocationId"
                  required
                  defaultValue={editing.defaultLocationId || ""}
                >
                  <option value="" disabled>
                    선택
                  </option>
                  {data.locations.map((item) => (
                    <option value={item.id} key={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label>
              권한
              <select
                name="role"
                defaultValue={editing.role}
                disabled={editing.id === data.user.id}
              >
                <option value="employee">직원</option>
                <option value="admin">관리자</option>
              </select>
              {editing.id === data.user.id && (
                <small className="field-help">
                  현재 로그인한 관리자 권한은 해제할 수 없어요.
                </small>
              )}
            </label>
            {editing.authDiagnostics && (
              <div className="onboarding-info-card" aria-label="로그인 진단">
                <KeyRound />
                <div>
                  <strong>로그인 진단</strong>
                  <p>
                    비밀번호 형식 {editing.authDiagnostics.passwordFormatValid ? "정상" : "확인 필요"}
                    {` · 활성 세션 ${editing.authDiagnostics.activeSessionCount}개`}
                  </p>
                  <small>
                    마지막 비밀번호 변경: {editing.authDiagnostics.lastPasswordChangedAt
                      ? formatSmsLogTime(editing.authDiagnostics.lastPasswordChangedAt)
                      : "기록 없음"}
                    <br />
                    마지막 로그인 성공: {editing.authDiagnostics.lastLoginSucceededAt
                      ? formatSmsLogTime(editing.authDiagnostics.lastLoginSucceededAt)
                      : "기록 수집 전"}
                    <br />
                    마지막 비밀번호 불일치: {editing.authDiagnostics.lastLoginFailedAt
                      ? formatSmsLogTime(editing.authDiagnostics.lastLoginFailedAt)
                      : "기록 없음"}
                  </small>
                </div>
              </div>
            )}
            <div className="dialog-actions">
              <button
                type="button"
                className="outline-button"
                onClick={() => setEditing(null)}
              >
                취소
              </button>
              <button className="primary-button">
                <Save size={17} /> 저장
              </button>
            </div>
          </form>
        </div>
      )}
      <section className="admin-panel table-panel employee-table-panel">
        <div className="section-title">
          <h2>{employmentView === "active" ? "재직 직원" : "퇴사자"}</h2>
          <span>{query || organizationFilter || locationFilter ? `검색 결과 ${filteredUsers.length}명` : `${employmentView === "active" ? activeUserCount : inactiveUserCount}명`}</span>
        </div>
        <div className="employee-status-tabs" role="group" aria-label="직원 상태">
          <button type="button" aria-pressed={employmentView === "active"} onClick={() => showEmploymentView("active")}>재직자 {activeUserCount}</button>
          <button type="button" aria-pressed={employmentView === "inactive"} onClick={() => showEmploymentView("inactive")}>퇴사자 {inactiveUserCount}</button>
        </div>
        <div className="employee-list-tools">
          <label className="employee-search">
            이름 검색
            <input
              type="search"
              value={query}
              placeholder="이름 입력"
              onChange={(event) => { setQuery(event.target.value); setPage(1); }}
            />
          </label>
          <label>
            소속 필터
            <select value={organizationFilter} onChange={(event) => { setOrganizationFilter(event.target.value); setPage(1); }}>
              <option value="">전체 소속</option>
              {data.organizations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <label>
            기본 장소 필터
            <select value={locationFilter} onChange={(event) => { setLocationFilter(event.target.value); setPage(1); }}>
              <option value="">전체 장소</option>
              {data.locations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <label>
            정렬
            <select value={sort} onChange={(event) => { setSort(event.target.value); setPage(1); }}>
              <option value="name-asc">이름 오름차순</option>
              <option value="name-desc">이름 내림차순</option>
              <option value="organization-asc">소속순</option>
              <option value="location-asc">장소순</option>
            </select>
          </label>
        </div>
        {employmentView === "active" && selectedUserIds.length > 0 && (
          <div className="employee-bulk-editor" role="group" aria-label="선택 직원 일괄 작업">
            <strong>{selectedUserIds.length}명 선택됨</strong>
            <label>
              일괄 소속
              <select value={bulkOrganizationId} onChange={(event) => setBulkOrganizationId(event.target.value)}>
                <option value="">변경 안 함</option>
                {data.organizations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>
            <label>
              일괄 기본 장소
              <select value={bulkLocationId} onChange={(event) => setBulkLocationId(event.target.value)}>
                <option value="">변경 안 함</option>
                {data.locations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>
            <label>
              일괄 권한
              <select value={bulkRole} onChange={(event) => setBulkRole(event.target.value as "" | User["role"])}>
                <option value="">변경 안 함</option>
                <option value="employee">직원</option>
                <option value="admin">관리자</option>
              </select>
            </label>
            <div className="employee-bulk-actions">
              <button className="primary-button compact" disabled={bulkActionBusy} onClick={updateSelectedUsers}>
                선택 직원 수정
              </button>
              <button className="danger-button" disabled={bulkActionBusy || selectedIncludesAdmin} onClick={deactivateSelectedUsers}>
                <Trash2 size={17} /> 선택 직원 퇴사 처리
              </button>
            </div>
          </div>
        )}
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                {employmentView === "active" && <th className="selection-column">
                  <label className="employee-select">
                    <input
                      type="checkbox"
                      aria-label="현재 페이지 전체 선택"
                      checked={allPageUsersSelected}
                      onChange={togglePageSelection}
                    />
                  </label>
                </th>}
                <th>이름</th>
                <th>소속</th>
                <th>기본 장소</th>
                <th>이메일</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {pageUsers.map((user) => (
                <tr key={user.id}>
                  {employmentView === "active" && <td className="selection-column">
                    <label className="employee-select">
                      <input
                        type="checkbox"
                        aria-label={`${user.name} 선택`}
                        checked={selectedUserIds.includes(user.id)}
                        onChange={() => setSelectedUserIds((current) => current.includes(user.id)
                          ? current.filter((id) => id !== user.id)
                          : [...current, user.id])}
                      />
                    </label>
                  </td>}
                  <td>
                    <strong>{user.name}</strong>
                    {user.role === "admin" && (
                      <small className="tag">관리자</small>
                    )}
                    {user.active === false && (
                      <small className="tag">퇴사</small>
                    )}
                  </td>
                  <td>{user.organizationName || "—"}</td>
                  <td>{user.defaultLocationName || "—"}</td>
                  <td>{user.email}</td>
                  <td>
                    <div className="inline-actions">
                      <button
                        onClick={() => {
                          setOpen(false);
                          setEditing(user);
                        }}
                      >
                        수정
                      </button>
                      <button onClick={() => reset(user.id)}>
                        비밀번호 초기화
                      </button>
                      <button
                        aria-label={`${user.name} 초대 메일 다시 보내기`}
                        onClick={() => resendInvite(user)}
                      >
                        초대 재발송
                      </button>
                      {user.role !== "admin" && (
                        <button
                          className={user.active === false ? "" : "is-danger"}
                          aria-label={`${user.name} ${user.active === false ? "복귀 처리" : "퇴사 처리"}`}
                          onClick={() => changeEmploymentStatus(user)}
                        >
                          {user.active === false ? "복귀" : "퇴사"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {pageUsers.length === 0 && <tr><td colSpan={employmentView === "active" ? 6 : 5} className="empty-row">
                {employmentView === "inactive" ? "퇴사자가 없습니다." : "표시할 직원이 없습니다."}
              </td></tr>}
            </tbody>
          </table>
        </div>
        <nav className="employee-pagination" aria-label="직원 목록 페이지">
          <button
            type="button"
            className="outline-button"
            aria-label="이전 페이지"
            disabled={visiblePage === 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            <ChevronLeft size={18} /> 이전
          </button>
          <strong>{visiblePage} / {totalPages}</strong>
          <button
            type="button"
            className="outline-button"
            aria-label="다음 페이지"
            disabled={visiblePage === totalPages}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
          >
            다음 <ChevronRight size={18} />
          </button>
        </nav>
      </section>
    </>
  );
}

function PhoneInput({ defaultValue = "" }: { defaultValue?: string }) {
  return (
    <input
      name="phone"
      type="tel"
      inputMode="numeric"
      autoComplete="tel"
      maxLength={13}
      pattern="010-[0-9]{4}-[0-9]{4}"
      title="010-0000-0000 형식의 휴대폰 번호를 입력해 주세요."
      placeholder="010-0000-0000"
      defaultValue={formatKoreanMobile(defaultValue)}
      required
      onChange={(event) => {
        event.currentTarget.value = formatKoreanMobile(
          event.currentTarget.value,
        );
      }}
    />
  );
}

function WeekdayPicker({
  allDays,
  selectedDays,
  onAllDaysChange,
  onSelectedDaysChange,
}: {
  allDays: boolean;
  selectedDays: number[];
  onAllDaysChange: (checked: boolean) => void;
  onSelectedDaysChange: (days: number[]) => void;
}) {
  function toggleDay(day: number) {
    onSelectedDaysChange(
      selectedDays.includes(day)
        ? selectedDays.filter((item) => item !== day)
        : [...selectedDays, day].sort((a, b) => a - b),
    );
  }
  return (
    <fieldset className="weekday-picker">
      <legend>선택 가능 요일</legend>
      <label className="all-days-option">
        <input
          type="checkbox"
          checked={allDays}
          onChange={(event) => onAllDaysChange(event.target.checked)}
        />
        요일 제한 없음
      </label>
      {!allDays && (
        <div className="weekday-options">
          {WEEKDAY_OPTIONS.map((day) => (
            <label
              className={selectedDays.includes(day.value) ? "is-selected" : ""}
              key={day.value}
            >
              <input
                type="checkbox"
                aria-label={day.label}
                checked={selectedDays.includes(day.value)}
                onChange={() => toggleDay(day.value)}
              />
              <span aria-hidden="true">{day.short}</span>
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );
}

function AdminMenus() {
  const { t } = useI18n();
  const [menus, setMenus] = useState<Menu[]>([]);
  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState("🍽️");
  const [price, setPrice] = useState(String(DEFAULT_MEAL_PRICE));
  const [allDays, setAllDays] = useState(true);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [editing, setEditing] = useState<Menu | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmoji, setEditEmoji] = useState("");
  const [editPrice, setEditPrice] = useState(String(DEFAULT_MEAL_PRICE));
  const [editAllDays, setEditAllDays] = useState(true);
  const [editDays, setEditDays] = useState<number[]>([]);
  const [message, setMessage] = useState("");
  const load = useCallback(
    () => api<Menu[]>("/api/admin/menus").then(setMenus),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);
  async function add(event: FormEvent) {
    event.preventDefault();
    if (!allDays && !selectedDays.length) {
      setMessage("선택 가능한 요일을 한 개 이상 골라 주세요.");
      return;
    }
    const parsedPrice = parseMenuPrice(price);
    if (parsedPrice === null) {
      setMessage("식대 단가를 0원 이상의 정수로 입력해 주세요.");
      return;
    }
    try {
      await api(
        "/api/admin/menus",
        json("POST", {
          name,
          emoji,
          price: parsedPrice,
          availableDays: allDays ? [...ALL_WEEKDAYS] : selectedDays,
        }),
      );
      setName("");
      setEmoji("🍽️");
      setPrice(String(DEFAULT_MEAL_PRICE));
      setAllDays(true);
      setSelectedDays([]);
      setMessage("메뉴를 추가했어요.");
      await load();
    } catch (problem) {
      setMessage((problem as Error).message);
    }
  }
  function openEdit(menu: Menu) {
    const days = menu.availableDays ?? [...ALL_WEEKDAYS];
    setEditing(menu);
    setEditName(menu.name);
    setEditEmoji(menu.emoji);
    setEditPrice(String(menu.price));
    setEditAllDays(days.length === ALL_WEEKDAYS.length);
    setEditDays(days.length === ALL_WEEKDAYS.length ? [] : [...days]);
    setMessage("");
  }
  async function saveEdit(event: FormEvent) {
    event.preventDefault();
    if (!editing) return;
    if (!editAllDays && !editDays.length) {
      setMessage("선택 가능한 요일을 한 개 이상 골라 주세요.");
      return;
    }
    const parsedPrice = parseMenuPrice(editPrice);
    if (parsedPrice === null) {
      setMessage("식대 단가를 0원 이상의 정수로 입력해 주세요.");
      return;
    }
    try {
      await api(
        `/api/admin/menus/${editing.id}`,
        json("PUT", {
          name: editName,
          emoji: editEmoji,
          price: parsedPrice,
          active: Boolean(editing.active),
          availableDays: editAllDays ? [...ALL_WEEKDAYS] : editDays,
        }),
      );
      setEditing(null);
      setMessage("메뉴 정보를 수정했어요.");
      await load();
    } catch (problem) {
      setMessage((problem as Error).message);
    }
  }
  async function toggle(menu: Menu) {
    await api(
      `/api/admin/menus/${menu.id}`,
      json("PUT", { active: !menu.active }),
    );
    await load();
  }
  return (
    <>
      <AdminTitle eyebrow={t("admin.menus.eyebrow")} title={t("admin.menus.title")} />
      {message && <div className="success-banner">{message}</div>}
      <form className="admin-panel form-stack menu-create-form" onSubmit={add}>
        <div className="field-row">
          <label>
            아이콘
            <input
              className="emoji-input"
              value={emoji}
              onChange={(e) => setEmoji(e.target.value)}
            />
          </label>
          <label>
            메뉴 이름
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="새 메뉴 이름"
              required
            />
          </label>
        </div>
        <label>
          식대 단가
          <input
            type="number"
            min={0}
            step={100}
            inputMode="numeric"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            required
          />
        </label>
        <WeekdayPicker
          allDays={allDays}
          selectedDays={selectedDays}
          onAllDaysChange={setAllDays}
          onSelectedDaysChange={setSelectedDays}
        />
        <button className="primary-button">
          <Plus /> 메뉴 추가
        </button>
      </form>
      <section className="admin-panel">
        <div className="section-title">
          <h2>메뉴 목록</h2>
          <span>기본 단가 {DEFAULT_MEAL_PRICE.toLocaleString()}원 · 메뉴별 수정 가능</span>
        </div>
        <div className="menu-admin-grid">
          {menus.map((menu) => (
            <article className={menu.active ? "" : "is-inactive"} key={menu.id}>
              <span>{menu.emoji}</span>
              <div>
                <strong>{menu.name}</strong>
                <small>{menu.price.toLocaleString()}원</small>
                <small>{availableDaysLabel(menu.availableDays)}</small>
              </div>
              <div className="inline-actions">
                <button
                  type="button"
                  aria-label={`${menu.name} 수정`}
                  onClick={() => openEdit(menu)}
                >
                  수정
                </button>
                <button type="button" onClick={() => toggle(menu)}>
                  {menu.active ? "삭제" : "복구"}
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
      {editing && (
        <div className="modal-backdrop">
          <form
            role="dialog"
            aria-modal="true"
            aria-labelledby="menu-edit-title"
            className="edit-dialog form-stack"
            onSubmit={saveEdit}
          >
            <div className="section-title">
              <h2 id="menu-edit-title">메뉴 수정</h2>
              <button
                type="button"
                className="icon-button"
                aria-label="메뉴 수정 창 닫기"
                onClick={() => setEditing(null)}
              >
                <X />
              </button>
            </div>
            <div className="field-row">
              <label>
                아이콘
                <input
                  className="emoji-input"
                  value={editEmoji}
                  onChange={(event) => setEditEmoji(event.target.value)}
                />
              </label>
              <label>
                메뉴 이름
                <input
                  value={editName}
                  onChange={(event) => setEditName(event.target.value)}
                  required
                />
              </label>
            </div>
            <label>
              식대 단가
              <input
                type="number"
                min={0}
                step={100}
                inputMode="numeric"
                value={editPrice}
                onChange={(event) => setEditPrice(event.target.value)}
                required
              />
            </label>
            <WeekdayPicker
              allDays={editAllDays}
              selectedDays={editDays}
              onAllDaysChange={setEditAllDays}
              onSelectedDaysChange={setEditDays}
            />
            <div className="dialog-actions">
              <button
                type="button"
                className="outline-button"
                onClick={() => setEditing(null)}
              >
                취소
              </button>
              <button className="primary-button">
                <Save size={17} /> 메뉴 저장
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

function AdminNotices({ onChange }: { onChange: () => Promise<void> }) {
  const { t } = useI18n();
  const [notices, setNotices] = useState<Announcement[]>([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pinned, setPinned] = useState(true);
  const [files, setFiles] = useState<File[]>([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editPinned, setEditPinned] = useState(false);
  const [editFiles, setEditFiles] = useState<File[]>([]);
  const [deletedImageIds, setDeletedImageIds] = useState<string[]>([]);
  const [editBusy, setEditBusy] = useState(false);
  const load = useCallback(
    () => api<Announcement[]>("/api/admin/announcements").then(setNotices),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);
  async function add(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    form.set("title", title);
    form.set("body", body);
    form.set("pinned", String(pinned));
    try {
      await api("/api/admin/announcements", { method: "POST", body: form });
      setTitle("");
      setBody("");
      setFiles([]);
      formElement.reset();
      setMessage("공지를 올렸어요.");
      await Promise.all([load(), onChange()]);
    } catch (problem) {
      setMessage((problem as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function remove(id: string) {
    await api(`/api/admin/announcements/${id}`, json("DELETE"));
    await Promise.all([load(), onChange()]);
  }
  async function togglePinned(notice: Announcement) {
    await api(
      `/api/admin/announcements/${notice.id}`,
      json("PUT", { pinned: !Boolean(notice.pinned) }),
    );
    setMessage(
      notice.pinned
        ? "홈 상단 고정을 해제했어요."
        : "홈 상단에 고정했어요.",
    );
    await Promise.all([load(), onChange()]);
  }
  function openEdit(notice: Announcement) {
    setEditing(notice);
    setEditTitle(notice.title);
    setEditBody(notice.body);
    setEditPinned(Boolean(notice.pinned));
    setEditFiles([]);
    setDeletedImageIds([]);
  }
  async function saveEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editing) return;
    setEditBusy(true);
    setMessage("");
    const form = new FormData();
    form.set("title", editTitle);
    form.set("body", editBody);
    form.set("pinned", String(editPinned));
    editFiles.forEach((file) => form.append("images", file));
    deletedImageIds.forEach((imageId) => form.append("deleteImageIds", imageId));
    try {
      await api(`/api/admin/announcements/${editing.id}`, { method: "PUT", body: form });
      setEditing(null);
      setMessage("공지를 수정했어요.");
      await Promise.all([load(), onChange()]);
    } catch (problem) {
      setMessage((problem as Error).message);
    } finally {
      setEditBusy(false);
    }
  }
  return (
    <>
      <AdminTitle eyebrow={t("admin.notices.eyebrow")} title={t("admin.notices.title")} />
      {message && <div className="success-banner">{message}</div>}
      {editing && (
        <div className="modal-backdrop">
          <form
            className="edit-dialog form-stack notice-edit-dialog"
            onSubmit={saveEdit}
            role="dialog"
            aria-modal="true"
            aria-labelledby="notice-edit-title"
          >
            <div className="section-title">
              <h2 id="notice-edit-title">공지 수정</h2>
              <button type="button" className="icon-button" aria-label="공지 수정 닫기" onClick={() => setEditing(null)}>
                <X />
              </button>
            </div>
            <label>
              공지 제목
              <input value={editTitle} onChange={(event) => setEditTitle(event.target.value)} required />
            </label>
            <label>
              공지 내용
              <textarea value={editBody} onChange={(event) => setEditBody(event.target.value)} required />
            </label>
            {editing.images?.some((image) => !deletedImageIds.includes(image.id)) ? (
              <div className="notice-edit-images" aria-label="현재 첨부 사진">
                {editing.images
                  .filter((image) => !deletedImageIds.includes(image.id))
                  .map((image) => (
                    <div key={image.id}>
                      <img src={image.url} alt={image.fileName} />
                      <button
                        type="button"
                        className="danger-button"
                        aria-label={`${image.fileName} 삭제`}
                        onClick={() => setDeletedImageIds((current) => [...current, image.id])}
                      >
                        <Trash2 size={17} /> 삭제
                      </button>
                    </div>
                  ))}
              </div>
            ) : null}
            <label className="image-upload">
              <span><ImagePlus size={19} /> 수정 사진 추가</span>
              <input
                aria-label="수정 사진 추가"
                type="file"
                accept="image/*"
                multiple
                onChange={(event) => setEditFiles(Array.from(event.target.files ?? []))}
              />
              <small>{editFiles.length ? `${editFiles.length}장 추가 예정` : "새로 추가할 사진이 있을 때만 선택해 주세요."}</small>
            </label>
            <label className="check-label">
              <input type="checkbox" checked={editPinned} onChange={(event) => setEditPinned(event.target.checked)} />
              홈 화면 맨 위에 고정
            </label>
            <div className="dialog-actions">
              <button type="button" className="outline-button" onClick={() => setEditing(null)}>취소</button>
              <button type="submit" className="primary-button" disabled={editBusy}>
                <Save size={18} /> {editBusy ? "수정 중..." : "공지 수정하기"}
              </button>
            </div>
          </form>
        </div>
      )}
      <form className="admin-panel form-stack" onSubmit={add}>
        <div className="notice-compose">
          <span className="notice-icon">👩‍🍳</span>
          <div>
            <label>
              제목
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="예: 금요일 식사 장소 안내"
                required
              />
            </label>
            <label>
              내용
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="직원들이 바로 이해할 수 있게 적어 주세요."
                required
              />
            </label>
          </div>
        </div>
        <label className="image-upload">
          <span>
            <ImagePlus size={19} /> 사진 첨부
          </span>
          <input
            aria-label="사진 첨부"
            name="images"
            type="file"
            accept="image/*"
            multiple
            onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
          />
          <small>
            {files.length
              ? `${files.length}장 선택됨 · 사진은 10장까지 올릴 수 있어요.`
              : "식단 사진을 여러 장 한 번에 선택할 수 있어요."}
          </small>
        </label>
        <label className="check-label">
          <input
            type="checkbox"
            checked={pinned}
            onChange={(e) => setPinned(e.target.checked)}
          />{" "}
          홈 화면 맨 위에 고정
        </label>
        <button className="primary-button" disabled={busy}>
          <Megaphone /> {busy ? "올리는 중..." : "공지 올리기"}
        </button>
      </form>
      <section className="admin-panel">
        <div className="section-title">
          <h2>등록한 공지</h2>
          <span>{notices.length}건</span>
        </div>
        <div className="card-list">
          {notices.map((notice) => (
            <article className="notice-admin-card" key={notice.id}>
              <div className="list-card">
                <span className="notice-icon">👩‍🍳</span>
                <div>
                  <strong>{notice.title}</strong>
                  <p>{notice.body}</p>
                </div>
                <div className="notice-card-actions">
                  <button
                    className="icon-button"
                    aria-label={`${notice.title} 수정`}
                    onClick={() => openEdit(notice)}
                  >
                    <Pencil size={19} />
                  </button>
                  <button
                    className="icon-button"
                    aria-label={`${notice.title} 삭제`}
                    onClick={() => remove(notice.id)}
                  >
                    <Trash2 />
                  </button>
                </div>
              </div>
              {notice.images?.length ? (
                <div className="notice-image-gallery is-compact">
                  {notice.images.map((image, index) => (
                    <img
                      key={image.id}
                      src={image.url}
                      alt={`${notice.title} 첨부 이미지 ${index + 1}`}
                      loading="lazy"
                    />
                  ))}
                </div>
              ) : null}
              <label className="check-label notice-pin-toggle">
                <input
                  type="checkbox"
                  checked={Boolean(notice.pinned)}
                  onChange={() => togglePinned(notice)}
                />
                {notice.title} 홈 상단 고정
              </label>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}
